import { SendDocModal, DocView, EstimateModal, PhotoPanel, DocumentPanel } from './extraComponents.jsx';
import React from 'react';
import { useState, useEffect, useMemo, useRef} from 'react';
import { D, st, toArr, fmt, fmtDate, calcTotal, getToken, apiGet, apiPost, apiPut, apiDel,
         BID_STATUS, LABOR_ROLES, LABOR_FACTORS, UNITS, calcTax, taxLabel, getTaxRate } from '../constants';
import { BID_TEMPLATES, MAT_CATALOG, MAT_CATALOG_FLAT } from '../data.js';
import { Overlay, MHdr, Field, BidBadge, StatGrid, AiBidNotes } from './shared';

// These are passed in from the app via props or imported from data files
// BID_TEMPLATES, MAT_CATALOG, MAT_CATALOG_FLAT are declared globally in data.js

export default function BidBuilder({bids,setBids,clients,setClients,estimates,setEstimates,invoices,setInvoices,jobs,setJobs,clientName,fmt,fmtDate,markup,openInvoiceModal,matPrices,setMatPrices,matPriceHistory,setMatPriceHistory,apiSaveMatPrices,apiKey:aiConfigured,apiSaveBid,apiDeleteBid,apiUpdateBid,apiSaveEstimate,apiUpdateEstimate,apiSaveInvoice,navigateTo,setNavigateTo,companyInfo,apiSaveClient,calcTotal,openClientModal,aiBidUsage,updateAiBidUsage,deposits,apiSaveDeposit,apiDeleteDeposit,me,apiSaveJob}){
  const ci=companyInfo||{};
  const [detail,setDetail]=useState(null);
  const isOwner=me?.user?.role==='owner';
  const [depForm,setDepForm]=useState({amount:'',date:new Date().toISOString().slice(0,10),method:'Check',notes:''});
  const [depSaving,setDepSaving]=useState(false);
  const [convertModal,setConvertModal]=useState(null);
  const [convertDeposit,setConvertDeposit]=useState({required:true,type:'percent',amount:'50'});
  const [bidModal,setBidModal]=useState(null);
  const [bidData,setBidData]=useState({});
  const setBD=(k,v)=>setBidData(x=>({...x,[k]:v}));

  // Estimate modal state (for combined page)
  const [modal,setModal]=useState(null);
  const saveEstimateInline=async d=>{
    try{
      if(d.id){
        const u=await apiPut("/data/estimates/"+d.id,d);
        setEstimates(prev=>toArr(prev).map(e=>e.id===d.id?{...e,...d,...u,clientId:u.client_id||d.clientId}:e));
      }else{
        const c=await apiPost("/data/estimates",d);
        setEstimates(prev=>[...toArr(prev),{...c,clientId:c.client_id||d.clientId}]);
      }
      setModal(null);
    }catch(err){alert("Failed to save estimate: "+(err.message||err));}
  };
  const deleteEstimateInline=async id=>{
    if(!confirm("Delete this estimate?"))return;
    try{await apiDel("/data/estimates/"+id);setEstimates(prev=>toArr(prev).filter(e=>e.id!==id));setDetail(null);}
    catch(err){alert("Failed to delete: "+err.message);}
  };

  // New client inline modal

  // Labor rate settings (editable in bid)
  const defaultRates={master:{cost:60,bill:125},journeyman:{cost:35,bill:75},apprentice:{cost:22,bill:45}};

  const emptyBid=(template=null)=>{
    const t=template&&BID_TEMPLATES[template]?BID_TEMPLATES[template]:BID_TEMPLATES["Blank / Custom"];
    return {
      id:null,
      clientId:clients[0]?.id||null,
      number:`BID-${String(Math.max(0,...toArr(bids).map(b=>parseInt(b.number?.replace("BID-","")||0)))+1).padStart(3,"0")}`,
      date:new Date().toISOString().slice(0,10),
      template:template||"Blank / Custom",
      status:"Pending",
      description:t.description,
      laborHours:{master:t.laborHours.master,journeyman:t.laborHours.journeyman,apprentice:(t.laborHours.apprentice ?? t.laborHours.helper ?? 0)},
      laborRates:{master:{...defaultRates.master},journeyman:{...defaultRates.journeyman},apprentice:{...defaultRates.apprentice}},
      materials:t.materials.map((m,i)=>({...m,id:i+1})),
      permitFee:t.permitFee,
      overheadPct:15,
      profitPct:15,
      notes:"",
      estimateId:null,
      invoiceId:null,
    };
  };

  const openNewBid=()=>{setBidData(emptyBid());setBidModal("template");};
  const [scanPlans,setScanPlans]=useState(false);

  // AI Voice Bid state
  const [aiBidOpen,setAiBidOpen]=useState(false);
  const AI_BID_LIMIT=10;
  const [showAiPaywall,setShowAiPaywall]=useState(false);
  const aiBidUsed=aiBidUsage?.aiBidUsed||0;
  const aiBidBonus=aiBidUsage?.aiBidBonus||0;
  const aiBetaWhitelisted=!!aiBidUsage?.aiBetaWhitelisted;
  const aiBidIsTrial=aiBidUsage?.plan==='trial';
  const aiBidsRemaining=(!aiBidIsTrial||aiBetaWhitelisted)?Infinity:Math.max(0,(AI_BID_LIMIT+aiBidBonus)-aiBidUsed);
  const aiBidLimitReached=aiBidIsTrial&&!aiBetaWhitelisted&&aiBidUsed>=(AI_BID_LIMIT+aiBidBonus);
  const [aiBidStep,setAiBidStep]=useState("describe"); // describe | refine | materials | permit | done
  const [aiBidMaterials,setAiBidMaterials]=useState([]);
  const updateAiMat=(id,k,v)=>setAiBidMaterials(prev=>prev.map(m=>m.id===id?{...m,[k]:v}:m));
  const removeAiMat=(id)=>setAiBidMaterials(prev=>prev.filter(m=>m.id!==id));
  const addAiMat=()=>setAiBidMaterials(prev=>[...prev,{id:Date.now(),desc:"",qty:1,unit:"ea",cost:0}]);
  const [aiBidTranscript,setAiBidTranscript]=useState("");
  const [aiBidListening,setAiBidListening]=useState(false);
  const [aiBidVoiceError,setAiBidVoiceError]=useState("");
  const [refineVoiceError,setRefineVoiceError]=useState("");
  const [aiBidLoading,setAiBidLoading]=useState(false);
  const [aiBidResult,setAiBidResult]=useState(null);
  const [aiBidFollowUps,setAiBidFollowUps]=useState([]);
  const [aiBidAnswers,setAiBidAnswers]=useState({});
  const [aiBidError,setAiBidError]=useState("");
  const aiBidRecogRef=useRef(null);
  const aiBidClientId=useRef(null);
  const [aiBidClientTick,setAiBidClientTick]=useState(0);
  const [pkgImporting,setPkgImporting]=useState(false);
  const [pkgImportMsg,setPkgImportMsg]=useState("");
  const aiBidJobId=useRef(null);
  const [aiBidJobTick,setAiBidJobTick]=useState(0);
  const [newJobName,setNewJobName]=useState('');
  const [showNewJobInput,setShowNewJobInput]=useState(false);
  const [savingNewJob,setSavingNewJob]=useState(false);
  const [selectedJobId,setSelectedJobId]=useState('');
  const [pkgImportOk,setPkgImportOk]=useState(false);
  const [intakeBldg,setIntakeBldg]=useState('');
  const [intakeConst,setIntakeConst]=useState('');
  const [intakeCeil,setIntakeCeil]=useState('');
  const [intakePanel,setIntakePanel]=useState('');
  const [intakeLocs,setIntakeLocs]=useState([]);
  const pkgFileRef=useRef();

  const AI_RATES={
    hourly:markup?.masterRate||125,
    journeyman:markup?.journeymanRate||95,
    apprentice:(markup?.apprenticeRate ?? markup?.helperRate ?? 65),
    recessedCan:markup?.masterRate||125,
    panel:markup?.masterRate||135,
    switchOutlet:markup?.masterRate||125,
    permit:markup?.permitFee||500,
    matMarkup:markup?.Materials||20,
    overhead:markup?.overhead||15,
    profit:markup?.profit||10,
  };

  const matCatalogList=MAT_CATALOG_FLAT.map(m=>{ const p=(matPrices&&matPrices[m.desc]!==undefined)?matPrices[m.desc]:m.cost; return m.desc+" ($"+p+"/"+m.unit+")"; }).join("; ");

  const SYSTEM_PROMPT=`You are an expert electrical estimating assistant for ${companyInfo.name||"an electrical contractor"}, a licensed electrical contractor${companyInfo.licenseNum?" ("+companyInfo.licenseNum+")":""} in ${companyInfo.address||"Florida"}.

Standard rates:
- Hourly labor (master): $${AI_RATES.hourly}/hr
- Hourly labor (journeyman): $${AI_RATES.journeyman}/hr
- Hourly labor (apprentice): ${AI_RATES.apprentice}/hr
- Recessed can installation: $${AI_RATES.recessedCan} per can (labor only)
- Panel work / breaker: $${AI_RATES.panel} per breaker/circuit
- Switch or outlet: $${AI_RATES.switchOutlet} each
- Material markup: ${AI_RATES.matMarkup}%
- Overhead: ${AI_RATES.overhead}%
- Profit margin: ${AI_RATES.profit}%
- Permit fee when required: $${AI_RATES.permit}

You understand NEC (National Electrical Code) as the primary code authority. GFCI, AFCI, and all standard protection requirements are NEC requirements — never describe them as Florida-specific or state-specific code. Florida adopts the NEC with minor amendments. You also understand Lee County local requirements and common residential/commercial electrical work.

Materials catalog — when a material matches one of these, use its EXACT name and cost (do not invent your own price for catalog items); only create new items/costs when nothing here applies:
${matCatalogList}

Standard labor-time guidelines — BASE hours before job-type/wire/access multipliers. Sum for multi-task jobs; do not pad for combinations:
- Ceiling fan swap (existing fan-rated box, customer supplies fan): 0.75 hr base
- Ceiling fan new installation (no existing box): 1.25 hr base
- Recessed can light (install, existing/finished ceiling): 0.5 hr base
- Outlet or switch (install or replace, existing circuit): 0.4 hr base
- GFCI outlet swap (replacing existing outlet): 0.3 hr base
- New 20A circuit homerun from panel (up to 30 ft): 1.0 hr base; add 0.1 hr per additional 10 ft
- Each outlet added on that new circuit: 0.4 hr base
- Smoke detector swap: 0.25 hr base
- Panel breaker add (space available): 0.5 hr base
Example: fan swap + 2 GFCI outlets on new 20A homerun (20 ft) = 0.75 + 1.0 + 0.4 + 0.4 = 2.55 hr base

Job-specific rules — apply automatically without being asked:
- Ceiling fan swap/replacement: do NOT add a ceiling box or ceiling box hardware to materials. The existing box stays in place. Only include the new fan, wiring, or fan-rated switch if the description indicates those are needed.
- Recessed/can lighting is ambiguous — "recessed can" can mean either a traditional deep-housing can or a slim wafer/disc-style ultra-thin LED light (low ceiling clearance, increasingly common for retrofits). If the description just says "recessed lights" or "recessed cans" without specifying which, always include "Do you mean traditional recessed can lights (deep housing) or wafer/disc-style ultra-thin LED lights (low-profile, better for tight ceiling space)?" in followUpQuestions. Use the matching "LED Recessed Can" (4\"/6\", new construction or remodel/retrofit) or "LED Wafer/Disc Light" (4\"/6\") catalog item as the material for each fixture — both include their own housing/junction. Do NOT add a separate ceiling box, octagon box, or pancake box for either type.
- New light fixture switch control: When adding new light fixtures (recessed cans, wafer/disc lights, fixtures) and the job description does not mention how they will be switched, ALWAYS include a followUpQuestion asking whether a switch is needed to control the new fixtures, and if so, whether this is a brand-new switch location (which may require a larger box or different gang count) or will tie into an existing switch. Do NOT assume switching is already handled.
- New garage outlet: always include "Is this outlet recessed (flush-mounted in the wall) or surface-mounted (conduit and surface box on the wall)?" in followUpQuestions.
- GFCI circuit protection: When adding GFCI protection to multiple outlets on a single circuit, spec ONE GFCI outlet at the first position with the remaining outlets wired as non-GFCI slaves (downstream). Never list multiple GFCI outlets on the same circuit. Alternatively, spec a GFCI breaker + all standard non-GFCI outlets. Never spec 6 GFCI outlets for 6 outlets on one circuit.
- Outlet added to an EXISTING circuit: When a follow-up answer confirms a new outlet is being added to an EXISTING circuit (not a new homerun) in a GFCI-required location (bathroom, kitchen, garage, outdoor, crawlspace, unfinished basement), the existing circuit already has GFCI protection upstream. Spec the new outlet as a STANDARD (non-GFCI) outlet on the load side. NEVER add a second GFCI outlet or GFCI breaker in this scenario. Only spec a GFCI device when the outlet is the first device on a NEW circuit/homerun, or when explicitly told no GFCI protection exists upstream.
- Surface-mount boxes: Default to 4-square electrical boxes with RS (raised surface) covers for surface-mounted conduit work. Do NOT spec weatherproof boxes or weatherproof covers unless the location is explicitly outdoor or a wet/damp location per NEC.
- EMT/conduit wire: When EMT is in the materials list, list conductors individually by color — "THHN 12 AWG Black", "THHN 12 AWG White", and "THHN 12 AWG Green" (or bare copper ground). Do not use a single generic "THWN" entry when conduit is being run.
- Wire quantity on new circuits: When running a new circuit with multiple outlets and the wire run distance is unknown, always add to followUpQuestions: "What is the distance from the panel to the first outlet, and approximately how far apart are the outlets from each other?" Use the answer to calculate wire quantity accurately. Do not guess or use a large default.
- EMT conduit sizing: Size conduit per NEC Chapter 9 Table 1 (max 40% fill for 3+ conductors). Reference: 3x #12 THHN conductors (0.0133 sq in each = 0.040 sq in total) fits in 1/2" EMT (0.122 sq in at 40% fill). 3x #10 THHN (0.0211 sq in each = 0.063 sq in) also fits 1/2" EMT. Only upsize to 3/4" when fill calculations require it.
- Bathroom wire gauge: Bathroom lighting circuits use 14/2 NM wire on a 15A breaker. Only the dedicated bathroom GFCI outlet circuit requires 12/2 NM wire on a 20A breaker (NEC 210.11(C)(3)). Never default all bathroom work to 12/2 — lighting in a bathroom is 14/2/15A unless the scope explicitly says otherwise.
- Wire line items must match each circuit's own gauge: Never combine wire runs for different circuit types (e.g., a 14/2 lighting circuit and a 12/2 outlet circuit) into a single wire line item, even when both runs are in the same room. List each gauge/circuit as its own separate wire line item with its own footage.
- Switches do not need a new circuit: Installing, replacing, or adding a switch (single-pole, 3-way, 4-way, dimmer) does NOT require a new circuit homerun. A switch is wired on the same circuit as the load it controls via a switch leg. Never add a new circuit breaker or homerun line item solely because a switch is being installed. Only create a new circuit when the scope explicitly calls for running a new homerun from the panel.
- LED tape/strip light: When LED tape or strip light is in the job scope, you MUST include at least one LED driver/transformer in the materials list — LED tape cannot run without a driver. Use "Magnitude 24V LED Driver 40W" ($83.34/ea), "Magnitude 24V LED Driver 60W" ($90.00/ea), or "Magnitude 24V LED Driver 96W" ($99.75/ea) from the catalog. Always add to followUpQuestions: "How many LED drivers/transformers will be needed for the tape light runs, and what wattage?" Include a reasonable estimate in the initial materials list and adjust after the follow-up answer is received.
- Multiple switches in same room: When 2 or more switches control loads in the same room and no separate locations are specified, use ONE multi-gang old-work box (e.g. "2 Gang Old Work Box" for 2 switches, "3 Gang Old Work Box" for 3) plus one matching multi-gang plate. Do NOT list two separate single-gang old-work boxes — that is wrong. One physical box with multiple gangs replaces all individual single-gang boxes at that location.
- Old-work/remodel box count: Match the old-work (remodel) box quantity to the number of fixtures being installed at ceiling or wall locations, not the number of switches. Each pendant, fan, or surface fixture that needs a new ceiling box gets its own box. Switches share a box per the multi-gang rule above.
- Small consumables: NEVER list wire nuts, cable staples, Romex staples, electrical tape, or any similar small consumable as a separate line item — not even once. These are always covered under the single "Misc Hardware / Supplies (lot)" line item. If you are about to add wire nuts, staples, or tape as a line item, stop and do not add it. Only one Misc Hardware lot item should appear in the entire materials list.
- Plug mold / plug-in molding: When plug mold is in the job scope, always add to followUpQuestions: "Will the plug mold circuit be protected by a GFCI outlet at the head or a GFCI breaker at the panel?" Do NOT include any outlet devices (GFCI or standard) in the materials list until this question is answered — omit them entirely from the initial bid. Plug mold is itself the receptacle strip; there are zero downstream outlet devices on a plug mold run. Never add 20A outlets or GFCI outlets alongside plug mold. If follow-up answer is GFCI outlet: add one GFCI outlet at the head only. If GFCI breaker: no outlet devices at all.
- Kitchen counter receptacle circuits: Any circuit feeding kitchen counter receptacles or plug mold must use 12/2 NM wire on a 20A circuit (NEC 210.52(B)). Never use 14/2 or 14/3 for kitchen counter receptacle or plug mold wiring. Under-cabinet lighting on a dedicated lighting circuit may stay 14/2/15A.
- AFCI/GFCI breakers: Only include an AFCI or GFCI breaker in materials when a brand-new circuit homerun is explicitly being installed. Never add a breaker for work on existing circuits — adding fixtures, switches, or outlets to an existing circuit does not require a new breaker.

STEP 1 — Before writing the materials array, map every switch, outlet, fixture, and device to a physical location. Group all devices at the same wall or ceiling location into one box entry. Example: "living room wall: 2 switches → 1 two-gang old-work box". Use this location map to determine box type and gang count before adding any box to materials.

When generating a bid, return ONLY valid JSON, no markdown, no explanation. Schema:
{
  "description": "brief scope summary",
  "laborHours": {"master": hours},
  "materials": [{"desc": "item", "qty": number, "unit": "ea|ft|lot", "cost": unit_cost}],
  "permitRequired": false,
  "permitNote": "reason if permit likely needed",
  "followUpQuestions": ["question 1", "question 2"],
  "confidence": "high|medium|low",
  "notes": "any important observations"
}`;

  const voiceErrorMsg=err=>({
    "not-allowed":"Microphone access was blocked. Check your browser/device settings and try again.",
    "no-speech":"No speech detected. Try again and speak clearly.",
    "service-not-allowed":"Voice input isn't available on this device/browser. Try typing instead.",
    "network":"Network error during speech recognition. Check your connection.",
  }[err]||`Voice input error: ${err}. Try typing instead.`);

  const startAiBidListening=()=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR||aiBidRecogRef.current)return;
    setAiBidVoiceError("");
    const r=new SR();r.continuous=true;r.interimResults=true;r.lang="en-US";
    let final="";
    r.onstart=()=>setAiBidListening(true);
    r.onresult=e=>{
      let interim="";let fn="";
      for(let i=e.resultIndex;i<e.results.length;i++){
        if(e.results[i].isFinal)fn+=e.results[i][0].transcript;
        else interim+=e.results[i][0].transcript;
      }
      if(fn)final+=fn;
      setAiBidTranscript((aiBidTranscript?aiBidTranscript.trimEnd()+" ":"")+final+(interim?" "+interim:""));
    };
    r.onerror=e=>{
      if(e.error==="aborted")return;
      setAiBidListening(false);
      setAiBidVoiceError(voiceErrorMsg(e.error));
    };
    r.onend=()=>{setAiBidListening(false);aiBidRecogRef.current=null;};
    aiBidRecogRef.current=r;r.start();
  };
  const stopAiBidListening=()=>{
    if(aiBidRecogRef.current){aiBidRecogRef.current.stop();aiBidRecogRef.current=null;}
    setAiBidListening(false);
  };

  const startRefineListening=()=>{
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    if(!SR||refineRecogRef.current)return;
    setRefineVoiceError("");
    const r=new SR();r.continuous=true;r.interimResults=true;r.lang="en-US";
    const base=refineInput;
    let final="";
    r.onstart=()=>setRefineListening(true);
    r.onresult=e=>{
      let interim="";let fn="";
      for(let i=e.resultIndex;i<e.results.length;i++){
        if(e.results[i].isFinal)fn+=e.results[i][0].transcript;
        else interim+=e.results[i][0].transcript;
      }
      if(fn)final+=fn;
      setRefineInput((base?base.trimEnd()+" ":"")+final+(interim?" "+interim:""));
    };
    r.onerror=e=>{
      if(e.error==="aborted")return;
      setRefineListening(false);
      setRefineVoiceError(voiceErrorMsg(e.error));
    };
    r.onend=()=>{setRefineListening(false);refineRecogRef.current=null;};
    refineRecogRef.current=r;r.start();
  };
  const stopRefineListening=()=>{
    if(refineRecogRef.current){refineRecogRef.current.stop();refineRecogRef.current=null;}
    setRefineListening(false);
  };
  const generateAiBid=async(transcript,answers={})=>{
    if(!aiConfigured){setAiBidError("AI not available. Contact your administrator.");return;}
    setAiBidLoading(true);setAiBidError("");
    try{
      const intakeCtx=[intakeBldg&&`Building: ${intakeBldg}`,intakeConst&&`Construction: ${intakeConst}`,intakeCeil&&`Ceiling/access: ${intakeCeil}`,intakePanel&&`Panel: ${intakePanel}`,intakeLocs.length>0&&`NEC locations: ${intakeLocs.join(', ')}`].filter(Boolean).join('\n');
      const ctxPrefix=intakeCtx?`Job context:\n${intakeCtx}\n\n`:'';
      const userMsg=answers&&Object.keys(answers).length>0
        ?`${ctxPrefix}Job description: ${transcript}\n\nFollow-up answers:\n${Object.entries(answers).map(([q,a])=>`Q: ${q}\nA: ${a}`).join("\n")}\n\nNow generate the final refined bid JSON.`
        :`${ctxPrefix}Generate a bid for this job: ${transcript}`;
      const resp=await fetch("/api/ai/generate-bid",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+getToken()},
        body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:4000,system:SYSTEM_PROMPT,messages:[{role:"user",content:userMsg}]})
      });
      const data=await resp.json();
      if(data.aiBidUsage&&typeof updateAiBidUsage==="function")updateAiBidUsage(data.aiBidUsage);
      if(data.error){
        setAiBidError(data.error.type==="limit_reached"?data.error.message:"API error: "+data.error.message);
        setAiBidLoading(false);return;
      }
      const text=data.content.map(c=>c.text||"").join("");
      const clean=text.replace(/```json|```/g,"").trim();
      const parsed=JSON.parse(clean);
      setAiBidResult(parsed);
      setAiBidFollowUps(parsed.followUpQuestions||[]);
      // Populate editable materials list
      const mats=toArr(parsed.materials).map((m,i)=>({
        id:Date.now()+i,desc:m.desc,qty:Number(m.qty)||1,unit:m.unit||"ea",cost:getPrice(m.desc)||Number(m.cost)||0
      }));
      setAiBidMaterials(mats);
      if(answers&&Object.keys(answers).length>0){
        setAiBidStep("materials");
      } else {
        setAiBidStep(parsed.followUpQuestions?.length>0?"refine":"materials");
      }
    }catch(e){setAiBidError("Error generating bid. Check your description and try again.");}
    setAiBidLoading(false);
  };

  const applyAiBid=(withPermit)=>{
    if(!aiBidResult)return;
    const r=aiBidResult;
    const newBid={
      ...emptyBid("AI Voice Bid"),
      clientId:aiBidClientId.current||clients[0]?.id||null,
      description:r.description||aiBidTranscript,
      template:"AI Voice Bid",
      laborHours:{
        master:Number(r.laborHours?.master)||0,
        journeyman:0,
        apprentice:0,
      },
      materials:aiBidMaterials.filter(m=>m.desc.trim()).map((m,i)=>({
        id:i+1,desc:m.desc,qty:m.qty,unit:m.unit,cost:m.cost,
      })),
      permitFee:withPermit?AI_RATES.permit:0,
      notes:r.notes||"",
      aiSnapshot:{description:r.description,laborHours:r.laborHours,materials:r.materials,permitRequired:r.permitRequired,confidence:r.confidence},
    };
    setBidData(newBid);
    setBidModal("edit");
    setAiBidOpen(false);
    setAiBidStep("describe");setAiBidTranscript("");setAiBidResult(null);
    setAiBidAnswers({});setAiBidFollowUps([]);setAiBidMaterials([]);
  };
  const [planPages,setPlanPages]=useState([]);
  const [planScanning,setPlanScanning]=useState(false);
  const [planResult,setPlanResult]=useState(null);
  const [planError,setPlanError]=useState("");
  const [pdfLoading,setPdfLoading]=useState(false);
  const [pdfPages,setPdfPages]=useState([]); // all pages from PDF for selection
  const [selectedPdfPages,setSelectedPdfPages]=useState([]);
  const [showPdfPicker,setShowPdfPicker]=useState(false);
  const planFileRef=useRef();

  // Load pdf.js dynamically
  const loadPdfJs=()=>new Promise((resolve,reject)=>{
    if(window.pdfjsLib){resolve(window.pdfjsLib);return;}
    const script=document.createElement("script");
    script.src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.onload=()=>{
      window.pdfjsLib.GlobalWorkerOptions.workerSrc="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    script.onerror=reject;
    document.head.appendChild(script);
  });

  // Convert PDF page to base64 image
  const pdfPageToImage=async(page,scale=2.0)=>{
    const viewport=page.getViewport({scale});
    const canvas=document.createElement("canvas");
    canvas.width=viewport.width;
    canvas.height=viewport.height;
    const ctx=canvas.getContext("2d");
    await page.render({canvasContext:ctx,viewport}).promise;
    return canvas.toDataURL("image/jpeg",0.85);
  };

  const addPlanPage=async e=>{
    const files=Array.from(e.target.files);
    e.target.value="";
    for(const file of files){
      if(file.type==="application/pdf"){
        // Handle PDF — extract all pages and let user select
        setPdfLoading(true);setPlanError("");
        try{
          const pdfjsLib=await loadPdfJs();
          const arrayBuffer=await file.arrayBuffer();
          const pdf=await pdfjsLib.getDocument({data:arrayBuffer}).promise;
          const pages=[];
          for(let i=1;i<=pdf.numPages;i++){
            const page=await pdf.getPage(i);
            const dataUrl=await pdfPageToImage(page);
            pages.push({
              pageNum:i,
              url:dataUrl,
              b64:dataUrl.split(",")[1],
              type:"image/jpeg",
              name:`${file.name} — Page ${i}`,
            });
          }
          setPdfPages(pages);
          setSelectedPdfPages(pages.map((_,i)=>i)); // select all by default
          setShowPdfPicker(true);
        }catch(err){
          setPlanError("Error reading PDF: "+err.message+". Try uploading as images instead.");
        }
        setPdfLoading(false);
      } else {
        // Handle image file directly
        const reader=new FileReader();
        reader.onload=ev=>setPlanPages(prev=>[...prev,{b64:ev.target.result.split(",")[1],type:file.type||"image/jpeg",name:file.name,url:URL.createObjectURL(file)}]);
        reader.readAsDataURL(file);
      }
    }
  };

  const importSelectedPdfPages=()=>{
    const selected=pdfPages.filter((_,i)=>selectedPdfPages.includes(i));
    setPlanPages(prev=>[...prev,...selected]);
    setShowPdfPicker(false);
    setPdfPages([]);
    setSelectedPdfPages([]);
  };
  const importPackagePdf=async(file)=>{
    setPkgImporting(true);setPkgImportMsg("");setPkgImportOk(false);
    try{
      const pdfjsLib=await loadPdfJs();
      const ab=await file.arrayBuffer();
      const pdf=await pdfjsLib.getDocument({data:ab}).promise;
      const imgs=[];
      for(let i=1;i<=Math.min(pdf.numPages,4);i++){
        const page=await pdf.getPage(i);
        const dataUrl=await pdfPageToImage(page,1.5);
        imgs.push({type:"image",source:{type:"base64",media_type:"image/jpeg",data:dataUrl.split(",")[1]}});
      }
      const prompt="You are reading an electrical submittal (lighting package or switch gear package). Extract every material line item with quantities and unit costs.\n\nReturn ONLY a JSON array, no markdown. Each object:\n- \"desc\": item name\n- \"qty\": quantity as number\n- \"unit\": unit such as ea, lot, ft\n- \"cost\": unit cost as number (0 if not shown)\n\nOnly materials/fixtures/equipment. Skip labor, notes, headers.";
      const resp=await fetch("/api/ai/messages",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+getToken()},body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:2000,messages:[{role:"user",content:[...imgs,{type:"text",text:prompt}]}],context:"bid_pdf_import"})});
      const data=await resp.json();
      if(data.error)throw new Error(data.error.message||"AI error");
      const text=(data.content||[]).map(c=>c.text||"").join("");
      const parsed=JSON.parse(text.replace(/```json|```/g,"").trim());
      if(!Array.isArray(parsed)||!parsed.length)throw new Error("No items found in document.");
      const newItems=parsed.map(item=>({id:Date.now()+Math.random(),desc:String(item.desc||""),qty:Number(item.qty)||1,unit:String(item.unit||"ea"),cost:Number(item.cost)||0}));
      setAiBidMaterials(prev=>[...prev,...newItems]);
      setPkgImportMsg("Added "+newItems.length+" item"+(newItems.length!==1?"s":"")+" from PDF");
      setPkgImportOk(true);
    }catch(err){setPkgImportMsg("Import failed: "+err.message);}
    setPkgImporting(false);
  };

  const scanPlansForBid=async()=>{
    if(!planPages.length||!aiConfigured){setPlanError("Add at least one plan page.");return;}
    setPlanScanning(true);setPlanError("");setPlanResult(null);
    try{
      const imageContent=planPages.map(p=>({type:"image",source:{type:"base64",media_type:p.type,data:p.b64}}));
      const matList=MAT_CATALOG_FLAT.map(m=>m.desc).join(", ");
      const resp=await fetch("/api/ai/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+getToken()},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:4000,
          context:"bid_plan_scan",
          messages:[{role:"user",content:[
            ...imageContent,
            {type:"text",text:`You are an expert electrical estimator analyzing construction plans for a Florida licensed electrical contractor. 

Analyze these plans and generate a complete electrical bid.

Available materials (use exact names from this list where possible):
${matList}

Labor rates: Master Electrician $125/hr billed, Apprentice $50/hr billed.

Return ONLY a valid JSON object with NO markdown, NO explanation:
{
  "description": "Brief scope of work description",
  "jobType": "New Construction" or "Remodel" or "Service Work",
  "wireType": "Romex (NM)" or "MC Cable" or "EMT Conduit" or "PVC Conduit" or "Mixed",
  "permitFee": estimated permit cost as number,
  "laborHours": {
    "master": estimated master hours as number,
    "journeyman": 0,
    "apprentice": 0
  },
  "materials": [
    {"desc": "material name from list", "qty": quantity as number, "unit": "ea" or "ft" or "lot", "cost": unit cost as number}
  ],
  "notes": "Any important observations about the plans",
  "confidence": "high", "medium", or "low",
  "planSummary": "Brief description of what was found in the plans"
}`}
          ]}]
        })
      });
      const data=await resp.json();
      if(data.error){setPlanError("API error: "+data.error.message);setPlanScanning(false);return;}
      const text=data.content.map(c=>c.text||"").join("");
      const clean=text.replace(/```json|```/g,"").trim();
      const parsed=JSON.parse(clean);
      setPlanResult(parsed);
    }catch(e){setPlanError("Error analyzing plans. Try clearer photos or fewer pages at once.");}
    setPlanScanning(false);
  };

  const importPlanBid=()=>{
    if(!planResult)return;
    const newBid=emptyBid("Blank / Custom");
    const withPlan={
      ...newBid,
      description:planResult.description||"",
      jobType:planResult.jobType||"New Construction",
      wireType:planResult.wireType||"Romex (NM)",
      permitFee:planResult.permitFee||0,
      laborHours:planResult.laborHours||{master:0,journeyman:0,apprentice:0},
      materials:(planResult.materials||[]).map((m,i)=>({...m,id:i+1,cost:getPrice(m.desc)||m.cost||0})),
      notes:planResult.notes||"",
      template:"From Plans",
    };
    setBidData(withPlan);
    setBidModal("edit");
    setScanPlans(false);
    setPlanPages([]);setPlanResult(null);setPlanError("");
  };
  const openEditBid=b=>{setDetail(null);setBidData({...b,laborHours:{...b.laborHours},laborRates:{...b.laborRates},materials:b.materials.map(m=>({...m}))});setBidModal("edit");};

  const saveBid=async()=>{
    if(!bidData.clientId&&toArr(clients).length>0){
      alert("Please select a client before saving.");return;
    }
    if(!bidData.description?.trim()){
      alert("Please add a bid description before saving.");return;
    }
    await apiSaveBid(bidData);
    setBidModal(null);
    if(!bidData.id){
      const newBids=await apiGet("/data/bids");
      const mapped=newBids.map(b=>({...b,clientId:b.client_id||b.clientId,laborHours:b.labor_hours||{},laborRates:b.labor_rates||{},permitFee:b.permit_fee,overheadPct:b.overhead_pct,profitPct:b.profit_pct,estimateId:b.estimate_id}));
      setBids(mapped);
      const newest=mapped.reduce((a,b)=>a.id>b.id?a:b,mapped[0]);
      if(newest)setDetail({...newest,_type:"bid"});
    }
  };
  const deleteBid=id=>{apiDeleteBid(id);setDetail(null);};
  const refineWithAi=async()=>{
    if(!refineInput.trim())return;
    setRefineLoading(true);setRefineError("");setRefineResult(null);
    try{
      const b=refineModal;
      const matList=toArr(b.materials).map(m=>`- ${m.qty} ${m.unit} ${m.desc} @ $${m.cost}`).join("\n");
      const lh=b.laborHours||{};
      const ctx=`Current bid:\nDescription: ${b.description}\nMaterials:\n${matList||"(none)"}\nLabor: ${lh.master||0} master hrs, ${lh.journeyman||0} journeyman hrs, ${lh.apprentice ?? lh.helper ?? 0} apprentice hrs`;
      const schema='{"addMaterials":[{"desc":"...","qty":1,"unit":"ea","cost":0}],"removeMaterialDescs":["exact desc to remove"],"laborHours":{"master":n,"journeyman":n,"apprentice":n},"notes":"brief explanation"}';
      const userMsg=ctx+"\n\nContractor refinement: "+JSON.stringify(refineInput.trim())+"\n\nReturn ONLY valid JSON matching this schema, patch only what the refinement changes:\n"+schema+"\n\nSet laborHours to null if labor hours should not change.";
      const resp=await fetch("/api/ai/messages",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+getToken()},body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:4000,system:SYSTEM_PROMPT,messages:[{role:"user",content:userMsg}],context:"bid_refine"})});
      const data=await resp.json();
      if(data.error)throw new Error(data.error.message||"AI error");
      const text=(data.content||[]).map(c=>c.text||"").join("");
      const parsed=JSON.parse(text.replace(/```json|```/g,"").trim());
      setRefineResult(parsed);
    }catch(err){setRefineError("Refinement failed: "+err.message);}
    setRefineLoading(false);
  };
  const applyRefinement=async()=>{
    const b=refineModal;const patch=refineResult;
    const removes=toArr(patch.removeMaterialDescs).map(d=>d.toLowerCase());
    let mats=toArr(b.materials).filter(m=>!removes.includes(m.desc.toLowerCase()));
    const toAdd=toArr(patch.addMaterials).map((m,i)=>({id:Date.now()+i,desc:m.desc||"",qty:Number(m.qty)||1,unit:m.unit||"ea",cost:Number(m.cost)||0}));
    mats=[...mats,...toAdd];
    const laborHours=patch.laborHours!=null?patch.laborHours:b.laborHours;
    const updated={...b,materials:mats,laborHours};
    await apiUpdateBid(b.id,{materials:mats,laborHours});
    setBids(prev=>toArr(prev).map(x=>x.id===b.id?updated:x));
    if(detail?.id===b.id)setDetail(updated);
    setRefineModal(null);setRefineResult(null);setRefineInput("");
  };

  // Bid photo helpers
  const addBidPhoto=async(bidId,file,caption="")=>{
    const compressed=await compressImage(file).catch(()=>file);
    return new Promise((resolve,reject)=>{
      const reader=new FileReader();
      reader.onload=async ev=>{
        const photo={id:Date.now(),url:ev.target.result,caption,addedAt:new Date().toISOString(),filename:file.name,
          originalSize:Math.round(file.size/1024)+"KB",compressedSize:Math.round(compressed.size/1024)+"KB"};
        const b=toArr(bids).find(x=>x.id===bidId);
        const photos=[...toArr(b?.photos||[]),photo];
        await apiUpdateBid(bidId,{photos});
        setBids(prev=>toArr(prev).map(x=>x.id===bidId?{...x,photos}:x));
        if(detail?.id===bidId)setDetail(d=>({...d,photos}));
        resolve(photo);
      };
      reader.onerror=reject;
      reader.readAsDataURL(compressed);
    });
  };
  const deleteBidPhoto=async(bidId,photoId)=>{
    const b=toArr(bids).find(x=>x.id===bidId);
    const photos=toArr(b?.photos||[]).filter(p=>p.id!==photoId);
    await apiUpdateBid(bidId,{photos});
    setBids(prev=>toArr(prev).map(x=>x.id===bidId?{...x,photos}:x));
    if(detail?.id===bidId)setDetail(d=>({...d,photos}));
  };
  const addBidDocument=async(bidId,file)=>{
    const fd=new FormData();fd.append("file",file);fd.append("folder","bids");
    const res=await fetch("/api/files/upload",{method:"POST",headers:{Authorization:"Bearer "+getToken()},body:fd});
    if(!res.ok)throw new Error("Upload failed");
    const {key}=await res.json();
    const b=toArr(bids).find(x=>x.id===bidId);
    const documents=[...toArr(b?.documents||[]),{key,name:file.name,size:file.size,addedAt:new Date().toISOString()}];
    await apiPut("/data/bids/"+bidId,{documents});
    setBids(prev=>toArr(prev).map(x=>x.id===bidId?{...x,documents}:x));
    if(detail?.id===bidId)setDetail(d=>({...d,documents}));
  };
  const deleteBidDocument=async(bidId,key)=>{
    await apiDel("/files?key="+encodeURIComponent(key));
    const b=toArr(bids).find(x=>x.id===bidId);
    const documents=toArr(b?.documents||[]).filter(doc=>doc.key!==key);
    await apiPut("/data/bids/"+bidId,{documents});
    setBids(prev=>toArr(prev).map(x=>x.id===bidId?{...x,documents}:x));
    if(detail?.id===bidId)setDetail(d=>({...d,documents}));
  };

  // Outcome capture state
  const [outcomeModal,setOutcomeModal]=useState(null); // {id, status}
  const [outcomeData,setOutcomeData]=useState({});
  const [refineModal,setRefineModal]=useState(null);
  const [refineInput,setRefineInput]=useState("");
  const [refineLoading,setRefineLoading]=useState(false);
  const [refineResult,setRefineResult]=useState(null);
  const [refineError,setRefineError]=useState("");
  const [refineListening,setRefineListening]=useState(false);
  const refineRecogRef=useRef(null);
  useEffect(()=>{if(detail)window.scrollTo(0,0);},[detail?.id]);
  const setOD=(k,v)=>setOutcomeData(x=>({...x,[k]:v}));

  const LOST_REASONS=["Price too high","Client went with another contractor","Client decided not to proceed","No response from client","Lost to unlicensed contractor","Scope changed","Timing didn't work out","Other"];
  const LOST_FACTORS=["Labor rate","Materials cost","Permit fee","Total price","Overhead/profit margin","Response time","Other"];

  const updateStatus=async(id,status)=>{
    if(status==="Lost"){
      setOutcomeData({reason:"",factors:[],notes:"",clientFeedback:""});
      setDetail(null);setOutcomeModal({id,status});
      return;
    }
    await apiUpdateBid(id,{status});
    if(detail?.id===id)setDetail(d=>({...d,status}));
  };

  const skipMarkLost=async()=>{
    await apiUpdateBid(outcomeModal.id,{status:"Lost"});
    if(detail?.id===outcomeModal.id)setDetail(d=>({...d,status:"Lost"}));
    setOutcomeModal(null);
  };
  const confirmOutcome=async()=>{
    if(!outcomeModal)return;
    const {id,status}=outcomeModal;
    const outcome={...outcomeData,capturedAt:new Date().toISOString()};
    await apiUpdateBid(id,{status,outcome});
    if(detail?.id===id)setDetail(d=>({...d,status,outcome}));
    setOutcomeModal(null);setOutcomeData({});
  };
  // Material helpers
  const getPrice=(desc)=>{
    if(matPrices&&matPrices[desc]!==undefined)return matPrices[desc];
    const found=MAT_CATALOG_FLAT.find(m=>m.desc===desc);
    return found?found.cost:0;
  };
  const [matCategory,setMatCategory]=useState(Object.keys(MAT_CATALOG)[0]);
  const [matPicker,setMatPicker]=useState(false);

  const addMatFromCatalog=(item)=>{
    const price=getPrice(item.desc);
    setBidData(x=>({...x,materials:[...toArr(x.materials),{id:Math.max(0,...toArr(x.materials).map(m=>m.id),0)+1,desc:item.desc,qty:1,unit:item.unit,cost:price}]}));
    setMatPicker(false);
  };
  const addMatBlank=()=>setBidData(x=>({...x,materials:[...toArr(x.materials),{id:Math.max(0,...toArr(x.materials).map(m=>m.id),0)+1,desc:"",qty:1,unit:"ea",cost:0}]}));
  const updateMat=(id,k,v)=>setBidData(x=>({...x,materials:toArr(x.materials).map(m=>m.id===id?{...m,[k]:v}:m)}));
  const removeMat=id=>setBidData(x=>({...x,materials:toArr(x.materials).filter(m=>m.id!==id)}));

  // Scan invoice to update material prices
  const [scanPrices,setScanPrices]=useState(false);
  const [scanning,setScanning]=useState(false);
  const [scanImg,setScanImg]=useState(null);
  const [scanImgB64,setScanImgB64]=useState(null);
  const [scanImgType,setScanImgType]=useState("image/jpeg");
  const [scanSupplier,setScanSupplier]=useState("");
  const scanFileRef=useRef();

  const loadScanFile=e=>{
    const file=e.target.files[0];if(!file)return;
    setScanImg(URL.createObjectURL(file));setScanImgType(file.type||"image/jpeg");
    const reader=new FileReader();
    reader.onload=ev=>setScanImgB64(ev.target.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  const scanInvoiceForPrices=async()=>{
    if(!scanImgB64||!aiConfigured){return;}
    setScanning(true);
    try{
      const resp=await fetch("/api/ai/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+getToken()},
        body:JSON.stringify({
          model:"claude-haiku-4-5-20251001",max_tokens:1500,
          context:"bid_price_scan",
          messages:[{role:"user",content:[
            {type:"image",source:{type:"base64",media_type:scanImgType,data:scanImgB64}},
            {type:"text",text:`You are reading a supply house invoice for an electrical contractor. Extract every line item with its unit price.

Known materials list (match as closely as possible):
${MAT_CATALOG_FLAT.map(m=>m.desc).join(", ")}

Return ONLY a JSON array, no markdown. Each object:
- "desc": matched material name from the list above (exact match if possible)
- "unit": unit of measure
- "unitPrice": price per unit as a number
- "confidence": "high" or "low"

Example: [{"desc":"14/2 NM Wire","unit":"ft","unitPrice":0.38,"confidence":"high"}]

If a wire is sold by the roll, calculate per-foot price. Only include items you can match to the known materials list.`}
          ]}]
        })
      });
      const data=await resp.json();
      const text=data.content.map(c=>c.text||"").join("");
      const clean=text.replace(/```json|```/g,"").trim();
      const parsed=JSON.parse(clean);
      if(Array.isArray(parsed)&&parsed.length>0){
        const supplier=scanSupplier.trim()||"Unknown Supplier";
        const scanDate=new Date().toISOString().slice(0,10);
        const updates={...matPrices};
        const histUpdates={...(matPriceHistory||{})};
        parsed.forEach(item=>{
          if(item.desc&&item.unitPrice>0){
            const oldPrice=updates[item.desc]!==undefined?updates[item.desc]:(MAT_CATALOG_FLAT.find(m=>m.desc===item.desc)?.cost||0);
            updates[item.desc]=item.unitPrice;
            if(!histUpdates[item.desc])histUpdates[item.desc]=[];
            histUpdates[item.desc]=[{price:item.unitPrice,oldPrice,supplier,date:scanDate},...histUpdates[item.desc]].slice(0,10);
          }
        });
        if (apiSaveMatPrices) {
          try { await apiSaveMatPrices(updates, histUpdates); }
          catch(e) { alert("Failed to save prices: "+e.message); }
        } else {
          setMatPrices(updates);
          if(setMatPriceHistory)setMatPriceHistory(histUpdates);
        }
        alert(`✓ Updated ${parsed.length} price${parsed.length>1?"s":""} from ${supplier}`);
        setScanPrices(false);setScanImg(null);setScanImgB64(null);setScanSupplier("");
      } else {
        alert("No matching items found. Try a clearer photo.");
      }
    }catch(e){alert("Error scanning invoice. Check API key.");}
    setScanning(false);
  };

  // Calculations
  const calcBid=(b)=>{
    const matMarkup=(markup&&markup["Materials"])||20;
    const matActual=toArr(b.materials).reduce((s,m)=>s+(Number(m.qty)||0)*(Number(m.cost)||0),0);
    const matBilled=matActual*(1+matMarkup/100);
    const laborCost=LABOR_ROLES.reduce((s,r)=>s+(Number(b.laborHours?.[r.key])||0)*(Number(b.laborRates?.[r.key]?.cost)||defaultRates[r.key].cost),0);
    const laborBilled=LABOR_ROLES.reduce((s,r)=>s+(Number(b.laborHours?.[r.key])||0)*(Number(b.laborRates?.[r.key]?.bill)||defaultRates[r.key].bill),0);
    const permit=Number(b.permitFee)||0;
    const overhead=matBilled*(Number(b.overheadPct)||0)/100;
    const profit=(matBilled+overhead)*(Number(b.profitPct)||0)/100;
    const subtotal=matBilled+laborBilled+permit;
    const total=subtotal+overhead+profit;
    const tax=calcTax(total);
    return{matActual,matBilled,laborCost,laborBilled,permit,subtotal,overhead,profit,total,tax,grandTotal:total+tax};
  };

  // Rounded line items used both for converting to an estimate and for the bid list total,
  // so the displayed bid amount matches the estimate amount after conversion.
  const bidLineItems=calc=>[
    {desc:"Labor",qty:1,unit:"job",rate:Math.round(calc.laborBilled*100)/100},
    {desc:"Materials",qty:1,unit:"lot",rate:Math.round((calc.matBilled+calc.overhead+calc.profit)*100)/100},
    ...(calc.permit>0?[{desc:"Permit Fee",qty:1,unit:"ea",rate:Math.round(calc.permit*100)/100}]:[]),
  ];

  // Convert bid to estimate — opens deposit prompt first
  const convertToEstimate=b=>{
    setConvertDeposit({required:true,type:'percent',amount:'50'});
    setConvertModal(b);
  };

  const doConvertToEstimate=async(b,deposit)=>{
    const calc=calcBid(b);
    const newEst={
      id:null,clientId:b.clientId,
      number:b.number?.replace(/^BID-/,"EST-")||`EST-${String(Math.max(0,...toArr(estimates).map(e=>parseInt(e.number?.replace("EST-","")||0)))+1).padStart(3,"0")}`,
      date:new Date().toISOString().slice(0,10),status:"Draft",
      notes:b.description,
      items:bidLineItems(calc),
      depositRequired:!!deposit?.required,
      depositAmount:deposit?.required?(deposit.amount||""):"",
      depositType:deposit?.type||"percent",
    };
    const created=await apiPost("/data/estimates",newEst);
    const createdId=created.id;
    if(aiBidJobId.current){
      try{await apiPut('/data/jobs/'+aiBidJobId.current,{estimateId:createdId});}catch(_){}
      setJobs(prev=>toArr(prev).map(j=>j.id===aiBidJobId.current?{...j,estimateId:createdId,estimate_id:createdId}:j));
    }
    setEstimates(prev=>[...toArr(prev),{...created,clientId:created.client_id||newEst.clientId,depositRequired:!!created.deposit_required,depositAmount:created.deposit_amount||"",depositType:created.deposit_type||"percent"}]);
    setListFilter("all");
    await apiUpdateBid(b.id,{estimateId:createdId,status:"Won"});
    if(detail?.id===b.id)setDetail(d=>({...d,estimateId:createdId,status:"Won"}));
  };


const [sendModal,setSendModal]=useState(null);
  const allItems=useMemo(()=>{
    const bidItems=toArr(bids).map(b=>{
      const calc=calcBid(b);
      return{...b,_type:"bid",_amount:calcTotal(bidLineItems(calc)),_sort:b.date||""};
    });
    const estItems=toArr(estimates).map(e=>{
      const total=calcTotal(e.items);
      return{...e,_type:"estimate",_amount:total,_sort:e.date||""};
    });
    return [...bidItems,...estItems].sort((a,b)=>b._sort.localeCompare(a._sort));
  },[bids,estimates]);
  const [listFilter,setListFilter]=useState("all");
  useEffect(()=>{
    if(!navigateTo) return;
    if(navigateTo.type==="bid"){
      const b=toArr(bids).find(b=>String(b.id)===String(navigateTo.id));
      if(b){setDetail({...b,_type:"bid"});if(setNavigateTo)setNavigateTo(null);}
    }else if(navigateTo.type==="estimate"){
      const e=toArr(estimates).find(e=>String(e.id)===String(navigateTo.id));
      if(e){setDetail({_type:"estimate",...e});if(setNavigateTo)setNavigateTo(null);}
    }
  },[navigateTo]);
  // ── DETAIL VIEW ──
  // Approve estimate inline
  const approveEstimate=async e=>{
    let deposited=0;
    if(e.depositRequired){
      const total=calcTotal(e.items);
      const required=e.depositType==="percent"?total*(parseFloat(e.depositAmount)||0)/100:parseFloat(e.depositAmount)||0;
      deposited=toArr(deposits).filter(d=>d.estimate_id===e.id).reduce((s,d)=>s+(d.amount||0),0);
      if(required>0&&deposited<required){
        if(isOwner){
          if(!window.confirm(`This estimate requires a deposit of ${fmt(required)}, but only ${fmt(deposited)} has been recorded.\n\nApprove anyway? (Owner override)`))return;
        }else{
          alert(`This estimate requires a deposit of ${fmt(required)} before it can be approved. Only ${fmt(deposited)} has been recorded.\n\nRecord the deposit on this estimate, or ask the owner to override.`);
          return;
        }
      }
    }
    await apiUpdateEstimate(e.id,{status:"Approved"});
    setEstimates(prev=>toArr(prev).map(x=>x.id===e.id?{...x,status:"Approved"}:x));
    const depAmt=toArr(deposits).filter(d=>d.estimate_id===e.id).reduce((s,d)=>s+(d.amount||0),0);
    if(!toArr(jobs).some(j=>(j.estimateId||j.estimate_id)===e.id)) await createJobFromEstimate(e);
    await createInvoiceFromEstimate(e,depAmt);
  };

  const createJobFromEstimate=async e=>{
    const client=toArr(clients).find(c=>c.id===e.clientId);
    await apiSaveJob({
      title:`${clientName(e.clientId)} — ${e.number}`,
      clientId:e.clientId, estimateId:e.id, status:"Needs Scheduling",
      date:'', time:'', endDate:'', address:client?.address||'', description:e.notes||''
    });
  };

  const createInvoiceFromEstimate=async(e,depositAmt=0)=>{
    const items=[...toArr(e.items)];
    if(depositAmt>0) items.push({desc:'Deposit Received',qty:1,unit:'ea',rate:-depositAmt});
    const invNum=e.number?.replace(/^EST-/,'INV-')||`INV-${String(Math.max(0,...toArr(invoices).map(x=>parseInt(x.number?.replace('INV-','')||0)))+1).padStart(3,'0')}`;
    const today=new Date().toISOString().slice(0,10);
    const due=new Date(Date.now()+30*24*60*60*1000).toISOString().slice(0,10);
    await apiSaveInvoice({clientId:e.clientId,number:invNum,date:today,dueDate:due,status:'Draft',notes:e.notes||'',items});
  };

  // Open estimate modal (use existing modal infrastructure)
  const openEstimateModal=e=>{
    setModal({type:"estimate",data:e||{id:null,number:`EST-${String(Math.max(0,...toArr(estimates).map(x=>parseInt(x.number?.replace("EST-","")||0)))+1).padStart(3,"0")}`,date:new Date().toISOString().slice(0,10),status:"Draft",clientId:clients[0]?.id||null,items:[{desc:"",qty:1,unit:"job",rate:0}]}});
  };

  if(detail){
    // ── Estimate detail view ──────────────────────────────────────────────
    if(detail._type==="estimate"){
      const e=toArr(estimates).find(x=>x.id===detail.id)||detail;
      // if(!e){return null;}
      const estTotal=calcTotal(e.items);
      const estColors={Draft:"#9ca3af",Sent:D.accent,Approved:D.success,Paid:"#22c55e"};
      const badge=<span style={{background:(estColors[e.status]||"#9ca3af")+"22",color:estColors[e.status]||"#9ca3af",fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:6}}>{e.status}</span>;
      const estDeposits=toArr(deposits).filter(d=>d.estimate_id===e.id);
      const totalDeposited=estDeposits.reduce((s,d)=>s+(d.amount||0),0);
      const requiredDeposit=e.depositRequired?(e.depositType==="percent"?estTotal*(parseFloat(e.depositAmount)||0)/100:parseFloat(e.depositAmount)||0):0;
      const depositBalance=requiredDeposit-totalDeposited;
      const recordDeposit=async()=>{
        const amt=Number(depForm.amount);
        if(!amt)return;
        setDepSaving(true);
        try{
          await apiSaveDeposit({estimate_id:e.id,client_id:e.clientId,amount:amt,date:depForm.date,method:depForm.method,notes:depForm.notes});
          setDepForm(f=>({...f,amount:'',notes:''}));
          if(e.depositRequired&&requiredDeposit>0&&(totalDeposited+amt)>=requiredDeposit&&e.status!=="Approved"){
            await apiUpdateEstimate(e.id,{status:"Approved"});
            setEstimates(prev=>toArr(prev).map(x=>x.id===e.id?{...x,status:"Approved"}:x));
            if(!toArr(jobs).some(j=>(j.estimateId||j.estimate_id)===e.id)) await createJobFromEstimate(e);
            await createInvoiceFromEstimate(e,totalDeposited+amt);
          }else if(e.status==="Approved"&&!toArr(jobs).some(j=>(j.estimateId||j.estimate_id)===e.id)){
            await createJobFromEstimate(e);
          }
        }catch(err){}
        setDepSaving(false);
      };
      return(<div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
          <button style={st.btn()} onClick={()=>setDetail(null)}>← Back</button>
          <h1 style={{...st.h1,margin:0}}>{e.number}</h1>
          {badge}
          <button style={st.btn("primary")} onClick={()=>openEstimateModal(e)}>Edit</button>
          <button style={st.btn()} onClick={()=>setSendModal(e)}>✉ Send</button>
          {e.status!=="Approved"&&<button style={st.btn("success")} onClick={()=>approveEstimate(e)}>✓ Approve</button>}
          <button style={st.btn("danger")} onClick={()=>deleteEstimateInline(e.id)}>Delete</button>
        </div>

        <div style={{...st.card, marginBottom:16}}>
          <div style={{fontWeight:600, marginBottom:12}}>Deposit</div>
          {e.depositRequired && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:14}}>
              <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Required</div><div style={{fontSize:18,fontWeight:500}}>{fmt(requiredDeposit)}</div></div>
              <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Received</div><div style={{fontSize:18,fontWeight:500,color:totalDeposited>0?D.success:D.muted}}>{fmt(totalDeposited)}</div></div>
              <div><div style={{fontSize:11,color:D.muted,marginBottom:4}}>Remaining</div><div style={{fontSize:18,fontWeight:600,color:depositBalance>0?D.danger:D.success}}>{fmt(Math.max(0,depositBalance))}</div></div>
            </div>
          )}
          {!e.depositRequired && <div style={{color:D.muted,fontSize:13,marginBottom:14}}>No deposit required for this estimate.</div>}
          {estDeposits.length > 0 && (
            <div style={{overflowX:"auto"}}><table style={{...st.table, marginBottom:14}}>
              <thead><tr>{['Date','Amount','Method','Notes',''].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {estDeposits.map(dp=>(
                  <tr key={dp.id}>
                    <td style={st.td}>{fmtDate(dp.date)}</td>
                    <td style={st.td}>{fmt(dp.amount)}</td>
                    <td style={st.td}>{dp.method||'-'}</td>
                    <td style={st.td}>{dp.notes||'-'}</td>
                    <td style={st.td}>
                      {isOwner && <button onClick={()=>{if(confirm('Delete deposit?'))apiDeleteDeposit(dp.id);}} style={{...st.btn('danger'),fontSize:11,padding:'2px 8px'}}>Delete</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          {estDeposits.length===0 && <div style={{color:D.muted,fontSize:13,marginBottom:14}}>No deposits recorded yet.</div>}
          <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
            <div>
              <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Amount</div>
              <input type="number" min="0" step="0.01" placeholder="0.00" style={{...st.input,width:100}} value={depForm.amount} onChange={ev=>setDepForm(f=>({...f,amount:ev.target.value}))}/>
            </div>
            <div>
              <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Date</div>
              <input type="date" style={{...st.input,width:140}} value={depForm.date} onChange={ev=>setDepForm(f=>({...f,date:ev.target.value}))}/>
            </div>
            <div>
              <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Method</div>
              <select style={{...st.input,width:110}} value={depForm.method} onChange={ev=>setDepForm(f=>({...f,method:ev.target.value}))}>
                {['Cash','Check','Card','ACH','Other'].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div style={{flex:1,minWidth:120}}>
              <div style={{fontSize:11,color:D.muted,marginBottom:3}}>Notes</div>
              <input style={{...st.input,width:'100%'}} placeholder="optional" value={depForm.notes} onChange={ev=>setDepForm(f=>({...f,notes:ev.target.value}))}/>
            </div>
            <button onClick={recordDeposit} disabled={depSaving||!depForm.amount} style={{...st.btn('success'),opacity:(depSaving||!depForm.amount)?0.6:1}}>
              {depSaving?'Saving...':'Record Deposit'}
            </button>
          </div>
        </div>

        <DocView doc={e} clientName={clientName(e.clientId)} fmtDate={fmtDate} type="Estimate" companyInfo={companyInfo}/>
        {sendModal&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(480px,95vw)"}}>
          <MHdr title="Send Estimate" onClose={()=>setSendModal(null)}/>
          <SendDocModal doc={sendModal} type="Estimate"
            clientEmail={clients.find(c=>c.id===sendModal.clientId)?.email||""}
            clientPhone={clients.find(c=>c.id===sendModal.clientId)?.phone||""}
            onClose={()=>setSendModal(null)}
            onMarkSent={()=>doSendEstimate(sendModal)}
            companyInfo={companyInfo}/>
        </div></Overlay>}
      </div>);
    }

    // ── Bid detail view ───────────────────────────────────────────────────
    const b=toArr(bids).find(x=>x.id===detail.id)||detail;
    // if(!b){return null;}
    const calc=calcBid(b);
    const linkedEst=estimates.find(e=>e.id===b.estimateId);
    return(<div>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20,flexWrap:"wrap"}}>
        <button style={st.btn()} onClick={()=>setDetail(null)}>← Back</button>
        <h1 style={{...st.h1,margin:0}}>{b.number}</h1>
        <BidBadge status={b.status}/>
        <button style={st.btn("primary")} onClick={()=>{openEditBid(b);}}>Edit</button>
        <button style={{...st.btn(),background:D.accent+"22",border:`1px solid ${D.accent}55`,color:D.accent}} onClick={()=>{setRefineModal(b);setRefineInput("");setRefineResult(null);setRefineError("");}}>Refine with AI</button>
        {b.status==="Pending"&&<>
          <button style={st.btn("success")} onClick={()=>updateStatus(b.id,"Won")}>✓ Mark Won</button>
          <button style={st.btn("danger")} onClick={()=>updateStatus(b.id,"Lost")}>✗ Mark Lost</button>
        </>}
        {(b.status==="Won"||b.status==="Pending")&&(!b.estimateId||!linkedEst)&&
          <button style={{...st.btn("primary"),background:D.success}} onClick={()=>convertToEstimate(b)}>→ Convert to Estimate</button>}

        {linkedEst&&<><span style={{fontSize:12,color:D.success}}>✓ Estimate {linkedEst.number} created</span><button style={st.btn()} onClick={()=>setSendModal(linkedEst)}>✉ Send Estimate</button></>}
        <button style={st.btn("danger")} onClick={()=>deleteBid(b.id)}>Delete</button>
      </div>

      {/* Bid Summary Card */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div style={st.card}>
          <div style={st.h2}>Bid Details</div>
          {[["Client",clientName(b.clientId)],["Date",fmtDate(b.date)],["Template",b.template],["Status",null,<BidBadge status={b.status}/>]].map(([label,val,node])=>(
            <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${D.border}`}}>
              <span style={{color:D.muted,fontSize:13}}>{label}</span>
              <span style={{fontSize:13}}>{node||val}</span>
            </div>
          ))}
          {b.description&&<div style={{marginTop:10,fontSize:13,color:D.muted,fontStyle:"italic"}}>{b.description}</div>}
        </div>

        {/* Price breakdown */}
        <div style={st.card}>
          <div style={st.h2}>Price Breakdown</div>
          {[
            ["Labor (billed)",calc.laborBilled,D.text],
            ["Materials (w/ markup)",calc.matBilled,D.text],
            ["Permit Fees",calc.permit,D.text],
            ["Overhead ("+b.overheadPct+"%)",calc.overhead,D.muted],
            ["Profit ("+b.profitPct+"%)",calc.profit,D.warning],
            ["Subtotal",calc.total,D.text],
            ...(getTaxRate()>0?[[taxLabel(),calc.tax,D.muted]]:[]),
          ].map(([label,val,color])=>(
            <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${D.border}`}}>
              <span style={{color:D.muted,fontSize:12}}>{label}</span>
              <span style={{color,fontSize:12,fontWeight:label==="Subtotal"?600:400}}>{fmt(val)}</span>
            </div>
          ))}
          <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0 4px",borderTop:`2px solid ${D.border}`,marginTop:4}}>
            <span style={{fontWeight:700,fontSize:15}}>Total Bid Price</span>
            <span style={{fontWeight:700,fontSize:18,color:D.success}}>{fmt(calc.grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* Lost outcome summary */}
      {b.status==="Lost"&&b.outcome&&<div style={{...st.card,border:`1px solid ${D.danger}33`,background:D.danger+"08",marginBottom:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <span style={{fontSize:14}}>📊</span>
          <div style={{fontFamily:"'Syne',sans-serif",fontSize:13,fontWeight:600,color:D.danger}}>Loss Feedback Captured</div>
          <span style={{fontSize:10,color:D.muted,marginLeft:"auto"}}>{b.outcome.capturedAt?new Date(b.outcome.capturedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):""}</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,fontSize:12}}>
          {b.outcome.reason&&<div><span style={{color:D.muted}}>Reason: </span><span style={{color:D.text,fontWeight:500}}>{b.outcome.reason}</span></div>}
          {b.outcome.competitorPrice&&<div><span style={{color:D.muted}}>Competitor price: </span><span style={{color:D.danger,fontWeight:600}}>{fmt(Number(b.outcome.competitorPrice))}</span></div>}
          {(b.outcome.factors||[]).length>0&&<div style={{gridColumn:"1/-1"}}><span style={{color:D.muted}}>Flagged high: </span><span style={{color:D.text}}>{b.outcome.factors.join(", ")}</span></div>}
          {b.outcome.clientFeedback&&<div style={{gridColumn:"1/-1"}}><span style={{color:D.muted}}>Client said: </span><span style={{color:D.text,fontStyle:"italic"}}>"{b.outcome.clientFeedback}"</span></div>}
          {b.outcome.notes&&<div style={{gridColumn:"1/-1"}}><span style={{color:D.muted}}>Notes: </span><span style={{color:D.text}}>{b.outcome.notes}</span></div>}
        </div>
      </div>}

      {/* Internal cost breakdown */}
      <div style={st.card}>
        <div style={st.h2}>Internal Cost Analysis</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
          {[
            ["Your Labor Cost",calc.laborCost,D.danger],
            ["Materials Cost",calc.matActual,D.danger],
            ["Total Cost",calc.laborCost+calc.matActual+calc.permit,D.danger],
            ["Gross Profit",calc.grandTotal-(calc.laborCost+calc.matActual+calc.permit),D.success],
          ].map(([label,val,color])=>(
            <div key={label} style={{background:D.surface,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:D.muted,marginBottom:4}}>{label}</div>
              <div style={{fontSize:16,fontWeight:700,color}}>{fmt(val)}</div>
            </div>
          ))}
        </div>

        {/* Labor breakdown */}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:500,marginBottom:8,color:D.muted}}>Labor Breakdown</div>
          <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead><tr>{["Role","Hours","Your Cost/hr","Billed/hr","Your Cost","Billed"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>{LABOR_ROLES.map(r=>{
              const hrs=Number(b.laborHours?.[r.key])||0;
              const costRate=Number(b.laborRates?.[r.key]?.cost)||defaultRates[r.key].cost;
              const billRate=Number(b.laborRates?.[r.key]?.bill)||defaultRates[r.key].bill;
              if(hrs===0)return null;
              return(<tr key={r.key}>
                <td style={st.td}>{r.label}</td>
                <td style={st.td}>{hrs}h</td>
                <td style={{...st.td,color:D.danger}}>${costRate}/hr</td>
                <td style={{...st.td,color:D.success}}>${billRate}/hr</td>
                <td style={{...st.td,color:D.danger}}>{fmt(hrs*costRate)}</td>
                <td style={{...st.td,color:D.success,fontWeight:500}}>{fmt(hrs*billRate)}</td>
              </tr>);
            })}</tbody>
          </table></div>
        </div>

        {/* Materials breakdown */}
        <div>
          <div style={{fontSize:13,fontWeight:500,marginBottom:8,color:D.muted}}>Materials Breakdown</div>
          <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead><tr>{["Description","Qty","Unit","Unit Cost","Total Cost","Billed (w/markup)"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>{toArr(b.materials).map(m=>{
              const matMarkup=(markup&&markup["Materials"])||20;
              const cost=(Number(m.qty)||0)*(Number(m.cost)||0);
              return(<tr key={m.id}>
                <td style={st.td}>{m.desc}</td>
                <td style={st.td}>{m.qty}</td>
                <td style={st.td}>{m.unit}</td>
                <td style={{...st.td,color:D.danger}}>${Number(m.cost).toFixed(2)}</td>
                <td style={{...st.td,color:D.danger}}>{fmt(cost)}</td>
                <td style={{...st.td,color:D.success,fontWeight:500}}>{fmt(cost*(1+matMarkup/100))}</td>
              </tr>);
            })}</tbody>
          </table></div>
        </div>
      </div>

      {/* Customer-facing bid sheet */}
      <div style={{...st.card,maxWidth:700}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
          <div><div style={{fontSize:20,fontWeight:500,color:D.accent}}>⚡ {ci.name}</div><div style={{color:D.muted,fontSize:12}}>Lic# {ci.licenseNum} · {ci.address} · {ci.phone}</div></div>
          <div style={{textAlign:"right"}}><div style={{fontSize:16,fontWeight:500}}>BID {b.number}</div><div style={{fontSize:12,color:D.muted}}>Date: {fmtDate(b.date)}</div></div>
        </div>
        <div style={{marginBottom:16,padding:"10px 14px",background:D.surface,borderRadius:8}}><div style={{fontSize:11,color:D.muted}}>Prepared for</div><div style={{fontWeight:500}}>{clientName(b.clientId)}</div></div>
        {b.description&&<div style={{marginBottom:14,fontSize:13,color:D.muted,lineHeight:1.6}}><strong style={{color:D.text}}>Scope of Work:</strong> {b.description}</div>}
        <div style={{overflowX:"auto"}}><table style={{...st.table,marginBottom:12}}>
          <thead><tr>{["Description","Amount"].map(h=><th key={h} style={{...st.th,textAlign:h==="Amount"?"right":"left"}}>{h}</th>)}</tr></thead>
          <tbody>
            <tr><td style={st.td}>Labor</td><td style={{...st.td,textAlign:"right"}}>{fmt(calc.laborBilled)}</td></tr>
            <tr><td style={st.td}>Materials</td><td style={{...st.td,textAlign:"right"}}>{fmt(calc.matBilled)}</td></tr>
            {calc.permit>0&&<tr><td style={st.td}>Permits & Fees</td><td style={{...st.td,textAlign:"right"}}>{fmt(calc.permit)}</td></tr>}
          </tbody>
        </table></div>
        <div style={{marginLeft:"auto",width:220}}>
          {[["Subtotal",calc.total],...(getTaxRate()>0?[[taxLabel(),calc.tax]]:[]),["Total",calc.grandTotal]].map(([label,val],i)=>(
            <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderTop:i===2?"1px solid "+D.border:"none",fontWeight:i===2?600:400,fontSize:i===2?16:13}}>
              <span style={{color:i===2?D.text:D.muted}}>{label}</span>
              <span style={{color:i===2?D.success:D.text}}>{fmt(val)}</span>
            </div>
          ))}
        </div>
        <div style={{marginTop:16,padding:"10px 14px",background:D.surface,borderRadius:8,fontSize:11,color:D.muted,lineHeight:1.6}}>
          This bid is valid for 30 days from the date above. A deposit may be required to schedule work. Prices subject to change if scope of work changes.
        </div>
        <div style={{marginTop:24,display:"grid",gridTemplateColumns:"1fr 1fr",gap:32}}>
          <div><div style={{fontFamily:"'Dancing Script','Brush Script MT',cursive",fontSize:32,color:D.text,height:48,display:"flex",alignItems:"flex-end",paddingBottom:4}}>{ ci.ownerName||ci.name}</div><div style={{borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Authorized Signature · {ci.name}</div></div>
          <div><div style={{height:48}}></div><div style={{borderTop:"1px solid "+D.muted,paddingTop:6,fontSize:11,color:D.muted}}>Client Signature · Acceptance of Bid</div></div>
        </div>
      </div>

    {/* Bid Photos */}
    <div style={st.card}>
      <PhotoPanel jobId={b.id} photos={toArr(b.photos)} onAdd={addBidPhoto} onDelete={deleteBidPhoto}/>
    </div>
    <div style={st.card}>
      <DocumentPanel bidId={b.id} documents={toArr(b.documents)} onAdd={addBidDocument} onDelete={deleteBidDocument}/>
    </div>

    {sendModal&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(480px,95vw)"}}>
      <MHdr title="Send Estimate" onClose={()=>setSendModal(null)}/>
      <div style={{fontSize:13,color:D.muted,marginBottom:16}}>
        <strong style={{color:D.text}}>{sendModal.number}</strong> · {clientName(sendModal.clientId)} · {fmt(calcTotal(sendModal.items))}
      </div>
      <SendDocModal doc={sendModal} type="Estimate"
        clientEmail={clients.find(c=>c.id===sendModal.clientId)?.email||""}
        clientPhone={clients.find(c=>c.id===sendModal.clientId)?.phone||""}
        onClose={()=>setSendModal(null)}
        onMarkSent={()=>doSendEstimate(sendModal)}
        companyInfo={companyInfo}/>
    </div></Overlay>}

    {/* Convert to Estimate — deposit prompt */}
    {convertModal&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(420px,95vw)"}}>
      <MHdr title="Convert to Estimate" onClose={()=>setConvertModal(null)}/>
      <div style={{fontSize:13,color:D.muted,marginBottom:14}}>Require a deposit before this estimate can be approved?</div>
      <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,cursor:"pointer"}}>
        <input type="checkbox" checked={convertDeposit.required} onChange={e=>setConvertDeposit(d=>({...d,required:e.target.checked}))} style={{accentColor:D.accent}}/>
        <span>Require deposit</span>
      </label>
      {convertDeposit.required&&<div style={{display:"flex",gap:8,alignItems:"center",marginBottom:16,flexWrap:"wrap"}}>
        <select style={{...st.input,width:110}} value={convertDeposit.type} onChange={e=>setConvertDeposit(d=>({...d,type:e.target.value}))}>
          <option value="percent">Percent</option>
          <option value="fixed">Fixed $</option>
        </select>
        <input type="number" min="0" step={convertDeposit.type==="percent"?"1":"0.01"} style={{...st.input,width:100}} value={convertDeposit.amount} onChange={e=>setConvertDeposit(d=>({...d,amount:e.target.value}))} placeholder={convertDeposit.type==="percent"?"%":"$"}/>
        {convertDeposit.type==="percent"&&<span style={{fontSize:12,color:D.success,fontWeight:600}}>{fmt(calcTotal(bidLineItems(calcBid(convertModal)))*(parseFloat(convertDeposit.amount)||0)/100)} due upfront</span>}
        {convertDeposit.type==="fixed"&&<span style={{fontSize:12,color:D.success,fontWeight:600}}>{fmt(parseFloat(convertDeposit.amount)||0)} due upfront</span>}
      </div>}
      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
        <button style={st.btn()} onClick={()=>setConvertModal(null)}>Cancel</button>
        <button style={st.btn("primary")} onClick={async()=>{const b=convertModal;const dep=convertDeposit;setConvertModal(null);await doConvertToEstimate(b,dep);}}>Continue</button>
      </div>
    </div></Overlay>}
    {/* Refine Bid Modal */}
    {refineModal&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(600px,97vw)",maxHeight:"90vh",overflowY:"auto"}}>
      <MHdr title="Refine Bid with AI" onClose={()=>{setRefineModal(null);setRefineResult(null);setRefineInput("");}}/>
      <div style={{background:D.surface,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:D.muted}}>
        <strong style={{color:D.text}}>{refineModal.number}</strong> &mdash; {refineModal.description}<br/>
        <span>{toArr(refineModal.materials).length} materials &bull; {refineModal.laborHours?.master||0} master hrs</span>
      </div>
      <Field label="What needs to change?">
        <textarea style={{...st.input,minHeight:80,resize:"vertical"}} placeholder='e.g. "Add a dedicated 20A circuit" or "Change fans from 3 to 5"' value={refineInput} onChange={e=>setRefineInput(e.target.value)} disabled={refineLoading}/>

      {(window.SpeechRecognition||window.webkitSpeechRecognition)&&<button style={{...st.btn(),marginTop:6,width:"100%",background:refineListening?D.danger+"22":"transparent",border:`1px solid ${refineListening?D.danger:D.border}`,color:refineListening?D.danger:D.muted}} onMouseDown={startRefineListening} onMouseUp={stopRefineListening} onMouseLeave={stopRefineListening} onTouchStart={e=>{e.preventDefault();startRefineListening();}} onTouchEnd={stopRefineListening} onTouchCancel={stopRefineListening}>{refineListening?"🎙 Listening… release to stop":"🎙 Hold to speak refinement"}</button>}
      {refineVoiceError&&<div style={{fontSize:12,color:D.danger,marginTop:4}}>{refineVoiceError}</div>}
      </Field>
      <button style={{...st.btn("primary"),width:"100%",marginTop:8}} onClick={refineWithAi} disabled={refineLoading||!refineInput.trim()}>
        {refineLoading?"Refining...":"Refine with AI"}
      </button>
      {refineError&&<div style={{color:D.danger,fontSize:12,marginTop:8}}>{refineError}</div>}
      {refineResult&&<div style={{marginTop:16}}>
        {refineResult.notes&&<div style={{background:D.accent+"11",border:`1px solid ${D.accent}33`,borderRadius:8,padding:"10px 14px",fontSize:12,marginBottom:12,color:D.muted}}>{refineResult.notes}</div>}
        {toArr(refineResult.addMaterials).length>0&&<div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:D.success,marginBottom:4}}>ADDING</div>
          {toArr(refineResult.addMaterials).map((m,i)=><div key={i} style={{fontSize:12,padding:"3px 0"}}><span style={{color:D.success}}>+</span> {m.qty} {m.unit} {m.desc} @ ${m.cost}</div>)}
        </div>}
        {toArr(refineResult.removeMaterialDescs).length>0&&<div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:D.danger,marginBottom:4}}>REMOVING</div>
          {toArr(refineResult.removeMaterialDescs).map((d,i)=><div key={i} style={{fontSize:12,padding:"3px 0",color:D.muted,textDecoration:"line-through"}}>{d}</div>)}
        </div>}
        {refineResult.laborHours!=null&&<div style={{fontSize:12,marginBottom:10}}><strong>Labor updated:</strong> {refineResult.laborHours.master} master / {refineResult.laborHours.journeyman} journeyman / {refineResult.laborHours.apprentice ?? refineResult.laborHours.helper ?? 0} apprentice hrs</div>}
        <div style={{display:"flex",gap:10,marginTop:12}}>
          <button style={{...st.btn("primary"),flex:1,background:D.success,border:"none"}} onClick={applyRefinement}>Apply Changes</button>
          <button style={st.btn()} onClick={()=>{setRefineResult(null);setRefineInput("");}}>Try Again</button>
        </div>
      </div>}
    </div></Overlay>}

    </div>);
  }

  // ── LIST VIEW ──
  const won=toArr(bids).filter(b=>b.status==="Won");
  const pending=toArr(bids).filter(b=>b.status==="Pending");
  const lost=toArr(bids).filter(b=>b.status==="Lost");
  const wonTotal=won.reduce((s,b)=>s+calcBid(b).grandTotal,0);
  const pendingTotal=pending.reduce((s,b)=>s+calcBid(b).grandTotal,0);

  // ── Combined status filters ──────────────────────────────────────────────
  // listFilter moved above

  // Merge bids and estimates into one pipeline
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above
  // allItems moved above

  const STATUS_FILTER_MAP={
    all:()=>true,
    pending:x=>x._type==="bid"&&x.status==="Pending",
    sent:x=>(x._type==="estimate"&&x.status==="Sent")||(x._type==="bid"&&x.status==="Sent"),
    approved:x=>x._type==="estimate"&&x.status==="Approved",
    won:x=>x._type==="bid"&&x.status==="Won",
    lost:x=>x._type==="bid"&&x.status==="Lost",
    draft:x=>x.status==="Draft",
  };

  const filtered=allItems.filter(STATUS_FILTER_MAP[listFilter]||STATUS_FILTER_MAP.all);

  const stats=[
    {label:"Pending Bids",value:toArr(bids).filter(b=>b.status==="Pending").length,color:D.accent},
    {label:"Pending Value",value:fmt(toArr(bids).filter(b=>b.status==="Pending").reduce((s,b)=>s+calcBid(b).grandTotal,0)),color:D.accent},
    {label:"Sent Estimates",value:toArr(estimates).filter(e=>e.status==="Sent").length,color:D.warning},
    {label:"Won / Approved",value:toArr(bids).filter(b=>b.status==="Won").length+toArr(estimates).filter(e=>e.status==="Approved").length,color:D.success},
  ];

  const PIPELINE_TABS=[
    {id:"all",label:"All"},
    {id:"pending",label:"📋 Bids"},
    {id:"sent",label:"✉ Sent"},
    {id:"approved",label:"✓ Approved"},
    {id:"won",label:"🏆 Won"},
    {id:"lost",label:"✗ Lost"},
    {id:"draft",label:"Draft"},
  ];

  const getItemBadge=x=>{
    if(x._type==="bid")return<BidBadge status={x.status}/>;
    const estColors={Draft:"#9ca3af",Sent:D.accent,Approved:D.success,Paid:"#22c55e"};
    return<span style={{background:(estColors[x.status]||"#9ca3af")+"22",color:estColors[x.status]||"#9ca3af",fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:6,border:`0.5px solid ${estColors[x.status]||"#9ca3af"}66`}}>{x.status}</span>;
  };

  const quickAction=x=>{
    if(x._type==="bid"){
      if(x.status==="Pending")return(
        <div style={{display:"flex",gap:4}} onClick={e=>e.stopPropagation()}>
          <button style={{...st.btn(),padding:"3px 8px",fontSize:11}} onClick={e=>{e.stopPropagation();openEditBid(x);}}>Edit</button>
          <button style={{...st.btn("success"),padding:"3px 8px",fontSize:11}} onClick={()=>updateStatus(x.id,"Won")}>Won</button>
          <button style={{...st.btn("danger"),padding:"3px 8px",fontSize:11}} onClick={()=>updateStatus(x.id,"Lost")}>Lost</button>
        </div>
      );
      if(x.status==="Won"&&!x.estimateId)return(
        <button style={{...st.btn("primary"),padding:"3px 8px",fontSize:11,marginLeft:0}} onClick={e=>{e.stopPropagation();convertToEstimate(x);}}>→ Estimate</button>
      );
    }
    if(x._type==="estimate"){
      if(x.status==="Draft"||x.status==="Sent")return(
        <div style={{display:"flex",gap:4}} onClick={e=>e.stopPropagation()}>
          <button style={{...st.btn(),padding:"3px 8px",fontSize:11}} onClick={()=>{openEstimateModal(x);}}>Edit</button>
          {x.status!=="Sent"&&<button style={{...st.btn("primary"),padding:"3px 8px",fontSize:11}} onClick={()=>setSendModal(x)}>✉ Send</button>}
          <button style={{...st.btn("success"),padding:"3px 8px",fontSize:11}} onClick={()=>approveEstimate(x)}>✓ Approve</button>
        </div>
      );
      if(x.status==="Approved")return(
        <button style={{...st.btn("primary"),padding:"3px 8px",fontSize:11,marginLeft:0}} onClick={e=>{e.stopPropagation();openInvoiceModal(null,x);}}>→ Invoice</button>
      );
    }
    return<button style={{...st.btn(),padding:"3px 8px",fontSize:11,marginLeft:0}} onClick={e=>{e.stopPropagation();x._type==="bid"?openEditBid(x):openEstimateModal(x);}}>Edit</button>;
  };

  // Estimate send modal state
  // sendModal moved above
  const doSendEstimate=async e=>{
    await apiUpdateEstimate(e.id,{status:"Sent"});
    setEstimates(prev=>toArr(prev).map(x=>x.id===e.id?{...x,status:"Sent"}:x));
    setSendModal(null);
  };


  return(<div>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
      <div style={st.h1}>📋 Bids & Estimates</div>
      <div style={{display:"flex",gap:8}}>
        <button style={{...st.btn(),marginLeft:0}} onClick={openEstimateModal}>+ New Estimate</button>
        <button style={{...st.btn("primary"),marginLeft:0}} onClick={openNewBid}>🎙 New Bid</button>
      </div>
    </div>

    <StatGrid stats={stats}/>

    {/* Pipeline filter tabs */}
    <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
      {PIPELINE_TABS.map(t=>(
        <button key={t.id} onClick={()=>setListFilter(t.id)}
          style={{padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",
            border:`1px solid ${listFilter===t.id?D.accent:D.border}`,
            background:listFilter===t.id?D.accent+"22":"transparent",
            color:listFilter===t.id?D.accent:D.muted,transition:"all 0.15s"}}>
          {t.label} {t.id!=="all"&&<span style={{opacity:0.7}}>({allItems.filter(STATUS_FILTER_MAP[t.id]||STATUS_FILTER_MAP.all).length})</span>}
        </button>
      ))}
    </div>

    {filtered.length===0
      ? <div style={{...st.card,textAlign:"center",padding:"40px 20px",color:D.muted}}>
          <div style={{fontSize:32,marginBottom:12}}>{listFilter==="all"?"📝":"🔍"}</div>
          <div style={{fontWeight:500,marginBottom:6}}>{listFilter==="all"?"No bids or estimates yet":"Nothing in this filter"}</div>
          <div style={{fontSize:13,marginBottom:16}}>{listFilter==="all"?"Start with an AI bid or create a manual estimate.":"Try a different filter tab."}</div>
          {listFilter==="all"&&<div style={{display:"flex",gap:8,justifyContent:"center"}}>
            <button style={{...st.btn(),marginLeft:0}} onClick={openEstimateModal}>+ New Estimate</button>
            <button style={{...st.btn("primary"),marginLeft:0}} onClick={openNewBid}>🎙 New Bid</button>
          </div>}
        </div>
      : <div style={st.card}>
          <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead><tr>{["#","Type","Client","Date","Amount","Status","Actions"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>{filtered.map(x=>(
              <tr key={x._type+x.id} style={{cursor:"pointer"}} onClick={()=>{x._type==="bid"?setDetail(x):setDetail(x);}}>
                <td style={{...st.td,color:D.accent}}>{x.number}</td>
                <td style={st.td}><span style={{fontSize:11,padding:"2px 7px",borderRadius:6,background:x._type==="bid"?D.warning+"22":D.accent+"22",color:x._type==="bid"?D.warning:D.accent,fontWeight:600}}>{x._type==="bid"?"Bid":"Estimate"}</span></td>
                <td style={st.td}>{clientName(x.clientId)}</td>
                <td style={{...st.td,color:D.muted,fontSize:12}}>{fmtDate(x.date)}</td>
                <td style={{...st.td,fontWeight:600,color:D.success}}>{fmt(x._amount)}</td>
                <td style={st.td}>{getItemBadge(x)}</td>
                <td style={st.td} onClick={e=>e.stopPropagation()}>{quickAction(x)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
    }


    {/* Convert to Estimate — deposit prompt */}
    {convertModal&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(420px,95vw)"}}>
      <MHdr title="Convert to Estimate" onClose={()=>setConvertModal(null)}/>
      <div style={{fontSize:13,color:D.muted,marginBottom:14}}>Require a deposit before this estimate can be approved?</div>
      <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,cursor:"pointer"}}>
        <input type="checkbox" checked={convertDeposit.required} onChange={e=>setConvertDeposit(d=>({...d,required:e.target.checked}))} style={{accentColor:D.accent}}/>
        <span>Require deposit</span>
      </label>
      {convertDeposit.required&&<div style={{display:"flex",gap:8,alignItems:"center",marginBottom:16,flexWrap:"wrap"}}>
        <select style={{...st.input,width:110}} value={convertDeposit.type} onChange={e=>setConvertDeposit(d=>({...d,type:e.target.value}))}>
          <option value="percent">Percent</option>
          <option value="fixed">Fixed $</option>
        </select>
        <input type="number" min="0" step={convertDeposit.type==="percent"?"1":"0.01"} style={{...st.input,width:100}} value={convertDeposit.amount} onChange={e=>setConvertDeposit(d=>({...d,amount:e.target.value}))} placeholder={convertDeposit.type==="percent"?"%":"$"}/>
        {convertDeposit.type==="percent"&&<span style={{fontSize:12,color:D.success,fontWeight:600}}>{fmt(calcTotal(bidLineItems(calcBid(convertModal)))*(parseFloat(convertDeposit.amount)||0)/100)} due upfront</span>}
        {convertDeposit.type==="fixed"&&<span style={{fontSize:12,color:D.success,fontWeight:600}}>{fmt(parseFloat(convertDeposit.amount)||0)} due upfront</span>}
      </div>}
      <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
        <button style={st.btn()} onClick={()=>setConvertModal(null)}>Cancel</button>
        <button style={st.btn("primary")} onClick={async()=>{const b=convertModal;const dep=convertDeposit;setConvertModal(null);await doConvertToEstimate(b,dep);}}>Continue</button>
      </div>
    </div></Overlay>}
    {/* Send Estimate Modal */}
    {sendModal&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(480px,95vw)"}}>
      <MHdr title="Send Estimate to Client" onClose={()=>setSendModal(null)}/>
      <div style={{fontSize:13,color:D.muted,marginBottom:16}}>
        <strong style={{color:D.text}}>{sendModal.number}</strong> · {clientName(sendModal.clientId)} · {fmt(calcTotal(sendModal.items))}
      </div>
      <SendDocModal doc={sendModal} type="Estimate"
        clientEmail={clients.find(c=>c.id===sendModal.clientId)?.email||""}
        clientPhone={clients.find(c=>c.id===sendModal.clientId)?.phone||""}
        onClose={()=>setSendModal(null)}
        onMarkSent={()=>doSendEstimate(sendModal)}
        companyInfo={companyInfo}/>
    </div></Overlay>}



    {/* Estimate modal — for creating/editing estimates inline */}
    {modal?.type==="estimate"&&<EstimateModal data={modal.data} clients={clients} onSave={saveEstimateInline} onClose={()=>setModal(null)} matPrices={matPrices} permits={[]} apiSaveClient={apiSaveClient} setClients={setClients}/>}

    {/* Template picker modal */}
    {bidModal==="template"&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(560px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
      <MHdr title="New Bid — Choose Starting Point" onClose={()=>setBidModal(null)}/>

      {/* AI Voice Bid — top option */}
      <div style={{background:aiBidLimitReached?`${D.surface}`:`linear-gradient(135deg,${D.warning}22,${D.accent}11)`,border:`1px solid ${aiBidLimitReached?D.border:`${D.warning}55`}`,borderRadius:10,padding:"16px 18px",marginBottom:12,cursor:"pointer",opacity:aiBidLimitReached?0.7:1}} onClick={()=>{
        if(aiBidLimitReached){setBidModal(null);setShowAiPaywall(true);return;}
        setBidModal(null);setAiBidOpen(true);setAiBidStep("describe");setAiBidTranscript("");setAiBidResult(null);setAiBidAnswers({});aiBidClientId.current=clients[0]?.id||null;
      }}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:28}}>{aiBidLimitReached?"🔒":"🎙"}</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:600,fontSize:14,color:aiBidLimitReached?D.muted:D.warning,marginBottom:3}}>AI Voice Bid — Describe the Job</div>
            <div style={{fontSize:12,color:D.muted}}>Speak the job out loud. Claude generates a full itemized bid instantly, then asks follow-up questions to refine it.</div>
          </div>
          {!aiBidLimitReached&&<div style={{flexShrink:0,background:D.warning+"22",border:`1px solid ${D.warning}44`,borderRadius:20,padding:"3px 10px",fontSize:11,color:D.warning,fontWeight:600,whiteSpace:"nowrap"}}>
            {aiBetaWhitelisted?"Unlimited":aiBidsRemaining} left
          </div>}
          {aiBidLimitReached&&<div style={{flexShrink:0,background:D.danger+"22",border:`1px solid ${D.danger}44`,borderRadius:20,padding:"3px 10px",fontSize:11,color:D.danger,fontWeight:600,whiteSpace:"nowrap"}}>
            Upgrade
          </div>}
        </div>
        {aiBidLimitReached&&<div style={{fontSize:12,color:D.danger,marginTop:8,padding:"6px 10px",background:D.danger+"11",borderRadius:6}}>
          ⚠ You've used all 3 free AI bids. Subscribe to AmpBooks for unlimited AI bidding.
        </div>}
        {!aiConfigured&&!aiBidLimitReached&&<div style={{fontSize:11,color:D.warning,marginTop:8}}>⚠ Requires API key in ⚙ Settings</div>}
      </div>

      {/* Scan Plans option - disabled for beta, accuracy not yet validated */}
      <div style={{background:D.surface,border:`1px solid ${D.border}`,borderRadius:10,padding:"16px 18px",marginBottom:16,opacity:0.6,cursor:"default"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:28}}>📐</span>
          <div>
            <div style={{fontWeight:600,fontSize:14,color:D.muted,marginBottom:3}}>Scan Plans — Auto-Generate Bid</div>
            <div style={{fontSize:12,color:D.muted}}>Upload photos or PDF of electrical plans and have AI generate a materials takeoff and labor estimate automatically.</div>
          </div>
        </div>
        <div style={{fontSize:11,color:D.muted,marginTop:8,fontWeight:600}}>🚧 Coming Soon</div>
      </div>

      <div style={{fontSize:12,color:D.muted,marginBottom:10}}>Or start from a template:</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        {Object.keys(BID_TEMPLATES).map(t=>(
          <button key={t} style={{...st.btn(),marginLeft:0,padding:"12px 16px",textAlign:"left",fontSize:13,height:"auto",background:D.surface,border:`1px solid ${D.border}`,borderRadius:8,cursor:"pointer",color:D.text}} onClick={()=>{setBidData(emptyBid(t));setBidModal("edit");}}>
            <div style={{fontWeight:500,marginBottom:4}}>{t}</div>
            <div style={{fontSize:11,color:D.muted}}>{BID_TEMPLATES[t].materials.length} materials · {Object.values(BID_TEMPLATES[t].laborHours).reduce((a,b)=>a+b,0)}h labor</div>
          </button>
        ))}
      </div>
    </div></Overlay>}

    {/* Scan Plans Modal */}
    {/* Lost bid outcome modal */}
    {outcomeModal?.status==="Lost"&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(520px,95vw)",maxHeight:"90vh",overflowY:"auto"}}>
      <MHdr title="✗ Mark Bid as Lost" onClose={()=>setOutcomeModal(null)}/>
      <div style={{background:D.danger+"11",border:`1px solid ${D.danger}33`,borderRadius:8,padding:"10px 14px",marginBottom:18,fontSize:13,color:D.muted,lineHeight:1.6}}>
        Before marking this lost, capture what happened. This data helps refine future AI bids — the more you record, the smarter the bidding gets.
      </div>

      {/* Primary reason */}
      <Field label="Why was this bid lost?">
        <select style={st.input} value={outcomeData.reason||""} onChange={e=>setOD("reason",e.target.value)}>
          <option value="">— Select a reason —</option>
          {LOST_REASONS.map(r=><option key={r}>{r}</option>)}
        </select>
      </Field>

      {/* Contributing factors */}
      <div style={{marginBottom:14}}>
        <label style={st.label}>Which line items were flagged as too high? (select all that apply)</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:6}}>
          {LOST_FACTORS.map(f=>{
            const selected=(outcomeData.factors||[]).includes(f);
            return(
              <button key={f} onClick={()=>{
                const cur=outcomeData.factors||[];
                setOD("factors",selected?cur.filter(x=>x!==f):[...cur,f]);
              }} style={{padding:"5px 12px",borderRadius:20,fontSize:12,cursor:"pointer",fontFamily:"'Syne',sans-serif",fontWeight:500,border:`1px solid ${selected?D.danger:D.border}`,background:selected?D.danger+"22":"transparent",color:selected?D.danger:D.muted,transition:"all 0.15s"}}>
                {f}
              </button>
            );
          })}
        </div>
      </div>

      {/* Client feedback */}
      <Field label="What did the client say? (optional)">
        <input style={st.input} value={outcomeData.clientFeedback||""} onChange={e=>setOD("clientFeedback",e.target.value)} placeholder='e.g. "Another guy quoted $400 less" or "Going with family friend"'/>
      </Field>

      {/* Competitor price */}
      {(outcomeData.reason==="Price too high"||outcomeData.reason==="Lost to unlicensed contractor"||(outcomeData.factors||[]).includes("Total price"))&&
        <Field label="Do you know what the competitor charged? (optional)">
          <input style={st.input} type="number" min="0" value={outcomeData.competitorPrice||""} onChange={e=>setOD("competitorPrice",e.target.value)} placeholder="$0.00"/>
        </Field>
      }

      {/* Notes */}
      <Field label="Any other notes?">
        <textarea style={{...st.input,height:68,resize:"vertical"}} value={outcomeData.notes||""} onChange={e=>setOD("notes",e.target.value)} placeholder="Anything else worth remembering about this one…"/>
      </Field>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:18}}>
        <button style={{...st.btn(),marginLeft:0,fontSize:12,color:D.muted}} onClick={skipMarkLost}>Skip — just mark lost</button>
        <button style={{...st.btn("danger"),marginLeft:0}} onClick={confirmOutcome}>
          ✗ Mark Lost & Save Feedback
        </button>
      </div>
    </div></Overlay>}

    {scanPlans&&<Overlay zIndex={150}><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(640px,97vw)",maxHeight:"94vh",overflowY:"auto"}}>
      <MHdr title="📐 Scan Plans — Auto-Generate Bid" onClose={()=>{setScanPlans(false);setPlanPages([]);setPlanResult(null);setPlanError("");}}/>

      <div style={{fontSize:13,color:D.muted,marginBottom:16,lineHeight:1.6}}>
        Upload photos or images of your electrical plans. You can add multiple pages. Claude will analyze them and generate a complete bid automatically — materials, quantities, labor hours, and permit costs.
      </div>

      {/* Upload area */}
      <input ref={planFileRef} type="file" accept="image/*,application/pdf" multiple style={{display:"none"}} onChange={addPlanPage}/>
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        <button style={{...st.btn("primary"),marginLeft:0}} onClick={()=>{planFileRef.current.removeAttribute("capture");planFileRef.current.click();}}>📁 Upload Plans (PDF or Image)</button>
        <button style={{...st.btn(),marginLeft:0}} onClick={()=>{planFileRef.current.setAttribute("capture","environment");planFileRef.current.click();}}>📷 Take Photo of Plan</button>
        {planPages.length>0&&<button style={{...st.btn("danger"),marginLeft:0}} onClick={()=>setPlanPages([])}>Clear All</button>}
      </div>

      {pdfLoading&&<div style={{textAlign:"center",padding:"16px 0",color:D.accent}}>
        <div style={{fontSize:24,marginBottom:8}}>📄</div>
        <div style={{fontWeight:500}}>Reading PDF…</div>
        <div style={{fontSize:12,color:D.muted,marginTop:4}}>Extracting pages, please wait</div>
      </div>}

      {/* PDF page selector */}
      {showPdfPicker&&<Overlay zIndex={250}><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(700px,97vw)",maxHeight:"90vh",overflowY:"auto"}}>
        <MHdr title="Select Plan Pages to Analyze" onClose={()=>{setShowPdfPicker(false);setPdfPages([]);setSelectedPdfPages([]);}}/>
        <div style={{fontSize:12,color:D.muted,marginBottom:14}}>
          Found <strong style={{color:D.text}}>{pdfPages.length} pages</strong>. Select the electrical plan pages to analyze — skip architectural, structural, or mechanical pages to save time and cost.
        </div>
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          <button style={{...st.btn(),marginLeft:0,fontSize:11}} onClick={()=>setSelectedPdfPages(pdfPages.map((_,i)=>i))}>Select All</button>
          <button style={{...st.btn(),marginLeft:0,fontSize:11}} onClick={()=>setSelectedPdfPages([])}>Deselect All</button>
          <span style={{fontSize:12,color:D.muted,alignSelf:"center"}}>{selectedPdfPages.length} of {pdfPages.length} selected</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12,marginBottom:16}}>
          {pdfPages.map((p,i)=>{
            const isSelected=selectedPdfPages.includes(i);
            return(
              <div key={i} onClick={()=>setSelectedPdfPages(prev=>prev.includes(i)?prev.filter(x=>x!==i):[...prev,i])}
                style={{cursor:"pointer",borderRadius:8,border:`2px solid ${isSelected?D.accent:D.border}`,overflow:"hidden",background:isSelected?D.accent+"11":D.surface}}>
                <img src={p.url} alt={`Page ${p.pageNum}`} style={{width:"100%",height:120,objectFit:"contain",background:"#fff"}}/>
                <div style={{padding:"6px 8px",display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:18}}>{isSelected?"✅":"⬜"}</span>
                  <span style={{fontSize:11,color:isSelected?D.accent:D.muted}}>Page {p.pageNum}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button style={st.btn()} onClick={()=>{setShowPdfPicker(false);setPdfPages([]);setSelectedPdfPages([]);}}>Cancel</button>
          <button style={st.btn("primary")} disabled={!selectedPdfPages.length} onClick={importSelectedPdfPages}>
            Add {selectedPdfPages.length} Page{selectedPdfPages.length!==1?"s":""} →
          </button>
        </div>
      </div></Overlay>}

      {/* Page thumbnails */}
      {planPages.length>0&&<div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>
        {planPages.map((p,i)=>(
          <div key={i} style={{position:"relative"}}>
            <img src={p.url} alt={`Page ${i+1}`} style={{width:120,height:90,objectFit:"cover",borderRadius:6,border:`1px solid ${D.border}`}}/>
            <button onClick={()=>setPlanPages(prev=>prev.filter((_,pi)=>pi!==i))} style={{position:"absolute",top:-6,right:-6,background:D.danger,color:"#fff",border:"none",borderRadius:"50%",width:18,height:18,cursor:"pointer",fontSize:10,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            <div style={{fontSize:9,color:D.muted,textAlign:"center",marginTop:2}}>Page {i+1}</div>
          </div>
        ))}
        <button style={{width:120,height:90,background:D.surface,border:`2px dashed ${D.border}`,borderRadius:6,cursor:"pointer",color:D.muted,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>{planFileRef.current.removeAttribute("capture");planFileRef.current.click();}}>+ Add page</button>
      </div>}

      {planPages.length===0&&<div style={{border:`2px dashed ${D.border}`,borderRadius:8,padding:"32px 20px",textAlign:"center",color:D.muted,marginBottom:14,cursor:"pointer"}} onClick={()=>{planFileRef.current.removeAttribute("capture");planFileRef.current.click();}}>
        <div style={{fontSize:32,marginBottom:8}}>📐</div>
        <div style={{fontWeight:500,marginBottom:4}}>No plan pages added yet</div>
        <div style={{fontSize:12}}>Click to upload or take photos of your plans</div>
      </div>}

      {planError&&<div style={{padding:"10px 14px",background:D.danger+"22",border:`1px solid ${D.danger}44`,borderRadius:8,color:D.danger,fontSize:13,marginBottom:14}}>{planError}</div>}

      {/* Scanning state */}
      {planScanning&&<div style={{textAlign:"center",padding:"24px 0"}}>
        <div style={{fontSize:32,marginBottom:12}}>🔍</div>
        <div style={{fontWeight:500,fontSize:14,color:D.accent,marginBottom:6}}>Analyzing plans…</div>
        <div style={{fontSize:12,color:D.muted}}>Claude is reading your plans and generating a materials takeoff. This may take 15-30 seconds.</div>
      </div>}

      {/* Results */}
      {planResult&&!planScanning&&<div style={{background:D.surface,borderRadius:10,padding:"16px",marginBottom:16,border:`1px solid ${D.success}44`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
          <div style={{fontWeight:600,fontSize:14,color:D.success}}>✓ Plans Analyzed</div>
          <span style={{fontSize:11,background:planResult.confidence==="high"?D.success+"22":planResult.confidence==="medium"?D.warning+"22":D.danger+"22",color:planResult.confidence==="high"?D.success:planResult.confidence==="medium"?D.warning:D.danger,padding:"2px 8px",borderRadius:6}}>
            {planResult.confidence} confidence
          </span>
        </div>
        {planResult.planSummary&&<div style={{fontSize:12,color:D.muted,marginBottom:10,fontStyle:"italic"}}>{planResult.planSummary}</div>}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:12}}>
          {[
            ["Job Type",planResult.jobType],
            ["Wire Type",planResult.wireType],
            ["Est. Labor",`${planResult.laborHours?.master||0} hrs`],
            ["Materials",`${(planResult.materials||[]).length} items`],
            ["Permit Fee",`$${planResult.permitFee||0}`],
          ].map(([label,val])=>(
            <div key={label} style={{background:D.card,borderRadius:6,padding:"8px 10px"}}>
              <div style={{fontSize:10,color:D.muted,marginBottom:2}}>{label}</div>
              <div style={{fontSize:13,fontWeight:500}}>{val}</div>
            </div>
          ))}
        </div>
        <div style={{fontSize:12,fontWeight:500,color:D.muted,marginBottom:6}}>Materials found:</div>
        <div style={{maxHeight:180,overflowY:"auto"}}>
          {(planResult.materials||[]).map((m,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${D.border}`,fontSize:12}}>
              <span>{m.desc} × {m.qty} {m.unit}</span>
              <span style={{color:D.danger}}>${((Number(m.qty)||0)*(Number(m.cost)||getPrice(m.desc)||0)).toFixed(2)}</span>
            </div>
          ))}
        </div>
        {planResult.notes&&<div style={{marginTop:10,fontSize:11,color:D.warning,background:D.warning+"11",padding:"8px 10px",borderRadius:6}}>📋 {planResult.notes}</div>}
      </div>}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
        <div style={{fontSize:11,color:D.muted}}>{planPages.length} page{planPages.length!==1?"s":""} uploaded{!aiConfigured?" · ⚠ API key required":""}</div>
        <div style={{display:"flex",gap:8}}>
          <button style={st.btn()} onClick={()=>{setScanPlans(false);setPlanPages([]);setPlanResult(null);setPlanError("");}}>Cancel</button>
          {planResult?
            <button style={st.btn("success")} onClick={importPlanBid}>→ Create Bid from Plans</button>:
            <button style={st.btn("primary")} disabled={!planPages.length||planScanning} onClick={scanPlansForBid}>🔍 Analyze Plans</button>
          }
          {planResult&&<button style={st.btn()} onClick={()=>{setPlanResult(null);setPlanError("");}}>Re-scan</button>}
        </div>
      </div>
    </div></Overlay>}

    {/* AI Voice Bid modal */}
    {aiBidOpen&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(560px,95vw)"}}>
      <MHdr title="🎙 AI Voice Bid" onClose={()=>{setAiBidOpen(false);if(aiBidRecogRef.current){aiBidRecogRef.current.stop();}}}/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:-8,marginBottom:12}}>
        <div style={{fontSize:11,background:aiBidsRemaining<=1?D.danger+"22":D.warning+"22",border:`1px solid ${aiBidsRemaining<=1?D.danger:D.warning}44`,borderRadius:20,padding:"3px 10px",color:aiBidsRemaining<=1?D.danger:D.warning,fontWeight:600}}>
          {aiBetaWhitelisted?"Unlimited AI bids":`${aiBidsRemaining} free AI bid${aiBidsRemaining!==1?"s":""} remaining`}
        </div>
      </div>
      {aiBidStep==="describe"&&<>
        <div style={{fontSize:13,color:D.muted,marginBottom:16,lineHeight:1.6}}>
          Describe the job out loud or type it. Be natural — <em>"Install 4 recess cans in family room, change 2-gang to 3-gang switch"</em>
        </div>

        {/* Client selector */}
        <Field label="Client">
          <select key={aiBidClientTick} style={st.input} defaultValue={clients[0]?.id||""} onChange={e=>{aiBidClientId.current=parseInt(e.target.value);aiBidJobId.current=null;setSelectedJobId('');setAiBidJobTick(t=>t+1);}}>
            {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <button style={{...st.btn(),whiteSpace:"nowrap",flexShrink:0,background:"#1e3a5f",border:"1px solid #2d5f9e",color:"#7eb8f7",marginTop:4}} onClick={()=>openClientModal(null,(c)=>{aiBidClientId.current=c.id;setAiBidClientTick(t=>t+1);})}>+ New Client</button>

        {/* Job selector */}
        <Field label="Job (optional)" style={{marginTop:8}}>
          <select style={st.input} value={selectedJobId}
            onChange={e=>{aiBidJobId.current=e.target.value?parseInt(e.target.value):null;setSelectedJobId(e.target.value);}}>
            <option value="">— No job —</option>
            {toArr(jobs).filter(j=>!aiBidClientId.current||(j.client_id||j.clientId)==aiBidClientId.current)
              .map(j=><option key={j.id} value={j.id}>{j.title||j.name||('Job #'+j.id)}</option>)}
          </select>
        </Field>
        {showNewJobInput ? (
          <div style={{display:'flex',gap:6,marginTop:4,marginBottom:8}}>
            <input style={{...st.input,flex:1,fontSize:13}} value={newJobName} autoFocus
              placeholder="Job title..." onChange={e=>setNewJobName(e.target.value)}
              onKeyDown={async e=>{
                if(e.key==='Escape'){setShowNewJobInput(false);setNewJobName('');return;}
                if(e.key!=='Enter'||!newJobName.trim())return;
                setSavingNewJob(true);
                try{const res=await apiPost('/data/jobs',{clientId:aiBidClientId.current,title:newJobName.trim(),status:'Needs Scheduling'});
                  setJobs(prev=>[...toArr(prev),{id:res.id,title:newJobName.trim(),client_id:aiBidClientId.current,status:'Needs Scheduling'}]);
                  aiBidJobId.current=res.id;setSelectedJobId(String(res.id));setAiBidJobTick(t=>t+1);setShowNewJobInput(false);setNewJobName('');}
                finally{setSavingNewJob(false);}
              }}
            />
            <button style={{...st.btn(),background:'#1e3a5f',border:'1px solid #2d5f9e',color:'#7eb8f7'}} disabled={savingNewJob}
              onClick={async()=>{
                if(!newJobName.trim())return;
                setSavingNewJob(true);
                try{const res=await apiPost('/data/jobs',{clientId:aiBidClientId.current,title:newJobName.trim(),status:'Needs Scheduling'});
                  setJobs(prev=>[...toArr(prev),{id:res.id,title:newJobName.trim(),client_id:aiBidClientId.current,status:'Needs Scheduling'}]);
                  aiBidJobId.current=res.id;setSelectedJobId(String(res.id));setAiBidJobTick(t=>t+1);setShowNewJobInput(false);setNewJobName('');}
                finally{setSavingNewJob(false);}
              }}>{savingNewJob?'…':'Save'}</button>
            <button style={st.btn()} onClick={()=>{setShowNewJobInput(false);setNewJobName('');}}>✕</button>
          </div>
        ) : (
          <button style={{...st.btn(),whiteSpace:'nowrap',background:'#1e3a5f',border:'1px solid #2d5f9e',color:'#7eb8f7',marginTop:4,marginBottom:8}}
            onClick={()=>setShowNewJobInput(true)}>+ New Job</button>
        )}

        {/* Job intake */}
        <div style={{marginTop:12,borderTop:'1px solid '+D.border,paddingTop:12}}>
          <div style={{fontSize:11,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:D.muted,marginBottom:10}}>Job Context</div>
          {[
            {label:'Building',key:'b',opts:['Residential','Light Commercial','Commercial'],val:intakeBldg,set:setIntakeBldg},
            {label:'Construction',key:'c',opts:['Existing / Retrofit','New Construction','Addition / Remodel'],val:intakeConst,set:setIntakeConst},
            {label:'Ceiling / access',key:'ce',opts:['Drywall + attic','Drywall, no attic','Open / unfinished','Concrete / slab'],val:intakeCeil,set:setIntakeCeil},
            {label:'Panel',key:'p',opts:['Has open breakers','Panel full','Panel upgrade needed','Unknown'],val:intakePanel,set:setIntakePanel},
          ].map(({label,key,opts,val,set})=>(
            <div key={key} style={{marginBottom:8}}>
              <div style={{fontSize:11,color:D.muted,marginBottom:4}}>{label}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                {opts.map(o=><button key={o} style={{padding:'3px 10px',borderRadius:20,border:'1px solid '+(val===o?D.accent:D.border),background:val===o?'rgba(249,115,22,0.12)':'transparent',color:val===o?D.accent:D.muted,fontSize:12,cursor:'pointer'}} onClick={()=>set(p=>p===o?'':o)}>{o}</button>)}
              </div>
            </div>
          ))}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:D.muted,marginBottom:4}}>NEC locations <span style={{opacity:.7}}>(all that apply)</span></div>
            <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
              {['Kitchen','Bathroom','Garage','Outdoor','Basement','Pool / Spa'].map(o=>(
                <button key={o} style={{padding:'3px 10px',borderRadius:20,border:'1px solid '+(intakeLocs.includes(o)?D.accent:D.border),background:intakeLocs.includes(o)?'rgba(249,115,22,0.12)':'transparent',color:intakeLocs.includes(o)?D.accent:D.muted,fontSize:12,cursor:'pointer'}}
                  onClick={()=>setIntakeLocs(p=>p.includes(o)?p.filter(l=>l!==o):[...p,o])}>{o}</button>
              ))}
            </div>
          </div>
        </div>
        {/* Voice input area */}
        <div style={{position:"relative",marginBottom:12,marginTop:8}}>
          <textarea
            style={{...st.input,width:"100%",height:100,resize:"none",fontSize:14,lineHeight:1.5,border:aiBidListening?`1.5px solid ${D.danger}`:`1px solid ${D.border}`}}
            placeholder="Describe the job here, or tap the mic…"
            value={aiBidTranscript}
            onChange={e=>setAiBidTranscript(e.target.value)}
          />
          {aiBidListening&&<div style={{position:"absolute",top:8,right:10,display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:7,height:7,borderRadius:"50%",background:D.danger,display:"inline-block",animation:"pulse 1s infinite"}}/>
            <span style={{fontSize:10,color:D.danger,fontWeight:600}}>LISTENING</span>
          </div>}
        </div>

        {/* Mic + generate buttons */}
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {(window.SpeechRecognition||window.webkitSpeechRecognition)&&<button
            style={{...st.btn(aiBidListening?"danger":"default"),marginLeft:0,padding:"10px 18px",fontSize:14,display:"flex",alignItems:"center",gap:8,border:aiBidListening?`2px solid ${D.danger}`:`1px solid ${D.border}`}}
            onMouseDown={startAiBidListening} onMouseUp={stopAiBidListening} onMouseLeave={stopAiBidListening}
            onTouchStart={e=>{e.preventDefault();startAiBidListening();}} onTouchEnd={stopAiBidListening} onTouchCancel={stopAiBidListening}
          >{aiBidListening?"⏹ Listening… release to stop":"🎙 Hold to speak"}</button>}
          <button
            style={{...st.btn("primary"),marginLeft:0,flex:1,padding:"10px 0",fontSize:14}}
            disabled={!aiBidTranscript.trim()||aiBidLoading}
            onClick={()=>generateAiBid(aiBidTranscript)}
          >{aiBidLoading?"⏳ Generating bid…":"⚡ Generate Bid"}</button>
        </div>
        {aiBidVoiceError&&<div style={{fontSize:12,color:D.danger,marginBottom:8}}>{aiBidVoiceError}</div>}
        {aiBidError&&<div style={{color:D.danger,fontSize:12,marginBottom:8}}>{aiBidError}</div>}
        {!aiConfigured&&<div style={{fontSize:12,color:D.warning,padding:"8px 12px",background:D.warning+"11",borderRadius:7,border:`1px solid ${D.warning}33`}}>⚠ API key required. Go to ⚙ Settings to add it.</div>}
      </>}

      {/* Step: Refine — follow-up questions */}
      {aiBidStep==="refine"&&aiBidResult&&<>
        <div style={{background:D.success+"11",border:`1px solid ${D.success}33`,borderRadius:8,padding:"10px 14px",marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:600,color:D.success,marginBottom:4}}>✓ Initial bid generated</div>
          <div style={{fontSize:13,color:D.text}}>{aiBidResult.description}</div>
          <div style={{fontSize:12,color:D.muted,marginTop:4}}>
            Labor: {(Number(aiBidResult.laborHours?.master)||0)}h · Materials: {toArr(aiBidResult.materials).length} items
          </div>
        </div>

        <div style={{fontSize:13,fontWeight:500,color:D.text,marginBottom:12}}>A few quick questions to sharpen the bid:</div>
        {aiBidFollowUps.map((q,i)=>(
          <div key={i} style={{marginBottom:14}}>
            <label style={{...st.label,color:D.text}}>{q}</label>
            <textarea
              style={{...st.input,height:52,resize:"none",fontSize:13}}
              placeholder="Your answer…"
              value={aiBidAnswers[q]||""}
              onChange={e=>setAiBidAnswers(x=>({...x,[q]:e.target.value}))}
            />
          </div>
        ))}

        {aiBidError&&<div style={{color:D.danger,fontSize:12,marginBottom:8}}>{aiBidError}</div>}
        <div style={{display:"flex",gap:8,marginTop:4}}>
          <button style={{...st.btn(),marginLeft:0}} onClick={()=>setAiBidStep("materials")}>Skip → Use initial bid</button>
          <button
            style={{...st.btn("primary"),marginLeft:0,flex:1}}
            disabled={aiBidLoading}
            onClick={()=>generateAiBid(aiBidTranscript,aiBidAnswers)}
          >{aiBidLoading?"⏳ Refining…":"✓ Refine Bid"}</button>
        </div>
      </>}

      {/* Step: Materials Review */}
      {aiBidStep==="materials"&&aiBidResult&&<>
        <div style={{background:D.success+"11",border:`1px solid ${D.success}33`,borderRadius:8,padding:"10px 14px",marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:600,color:D.success,marginBottom:2}}>✓ {aiBidResult.description}</div>
          <div style={{fontSize:12,color:D.muted}}>Review and edit materials below. Add anything Claude missed.</div>
        </div>

        {/* Materials header */}
        <div style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 36px",gap:6,marginBottom:6}}>
          {["Description","Qty","Unit","Unit Cost",""].map(h=>(
            <div key={h} style={{fontSize:11,color:D.muted,fontWeight:500}}>{h}</div>
          ))}
        </div>

        {/* Material rows */}
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10,maxHeight:280,overflowY:"auto"}}>
          {aiBidMaterials.map(m=>(
            <div key={m.id} style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 36px",gap:6,alignItems:"center"}}>
              <input style={{...st.input,fontSize:12}} value={m.desc} onChange={e=>updateAiMat(m.id,"desc",e.target.value)} placeholder="Material description"/>
              <input style={{...st.input,fontSize:12}} type="number" min="0" step="0.25" value={m.qty} onChange={e=>updateAiMat(m.id,"qty",e.target.value)}/>
              <select style={{...st.input,fontSize:12}} value={m.unit} onChange={e=>updateAiMat(m.id,"unit",e.target.value)}>
                {["ea","ft","box","roll","lot","bag","lb","hr"].map(u=><option key={u}>{u}</option>)}
              </select>
              <input style={{...st.input,fontSize:12}} type="number" min="0" step="0.01" value={m.cost} onChange={e=>updateAiMat(m.id,"cost",e.target.value)} placeholder="0.00"/>
              <button style={{background:D.danger,color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,height:32,width:36,minWidth:36,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}} onClick={()=>removeAiMat(m.id)}>✕</button>
            </div>
          ))}
          {aiBidMaterials.length===0&&(
            <div style={{fontSize:13,color:D.muted,padding:"12px 0",textAlign:"center"}}>No materials yet — add them below.</div>
          )}
        </div>

        {/* Add material row */}
        <button style={{...st.btn(),marginLeft:0,width:"100%",marginBottom:14,fontSize:12,border:`1px dashed ${D.border}`}} onClick={addAiMat}>
          + Add Material
        </button>
        <div style={{marginBottom:14}}>
          <input ref={pkgFileRef} type="file" accept="application/pdf" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)importPackagePdf(f);e.target.value="";}}/>
          <button style={{...st.btn(),marginLeft:0,width:"100%",fontSize:12,border:`1px dashed ${D.accent}55`,color:D.accent,background:D.accent+"11"}} onClick={()=>pkgFileRef.current?.click()} disabled={pkgImporting}>
            {pkgImporting?"Scanning PDF...":"Import Lighting / Switch Gear Package PDF"}
          </button>
          {pkgImportMsg&&<div style={{fontSize:11,marginTop:4,color:pkgImportOk?D.success:D.danger}}>{pkgImportMsg}</div>}
        </div>

        {/* Totals summary */}
        <div style={{background:D.surface,borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
            <span style={{color:D.muted}}>Materials cost (your cost)</span>
            <span style={{color:D.danger,fontWeight:600}}>{fmt(aiBidMaterials.reduce((s,m)=>s+(m.qty*m.cost),0))}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span style={{color:D.muted}}>Billed to client ({AI_RATES.matMarkup}% markup)</span>
            <span style={{color:D.success,fontWeight:600}}>{fmt(aiBidMaterials.reduce((s,m)=>s+(m.qty*m.cost*(1+AI_RATES.matMarkup/100)),0))}</span>
          </div>
        </div>

        <div style={{display:"flex",gap:8}}>
          <button style={{...st.btn(),marginLeft:0}} onClick={()=>setAiBidStep(aiBidFollowUps.length>0?"refine":"describe")}>← Back</button>
          <button style={{...st.btn("primary"),marginLeft:0,flex:1,padding:"10px 0",fontSize:14}} onClick={()=>setAiBidStep("permit")}>
            Next — Permit →
          </button>
        </div>
      </>}

      {/* Step: Permit */}
      {aiBidStep==="permit"&&aiBidResult&&<>
        <div style={{background:D.success+"11",border:`1px solid ${D.success}33`,borderRadius:8,padding:"12px 14px",marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:600,color:D.success,marginBottom:6}}>✓ Bid ready — {aiBidResult.description}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginTop:8}}>
            {[
              ["Master hrs",(Number(aiBidResult.laborHours?.master)||0)+"h"],
              ["Materials",aiBidMaterials.filter(m=>m.desc).length+" items"],
            ].map(([l,v])=>(
              <div key={l} style={{background:D.surface,borderRadius:6,padding:"6px 10px"}}>
                <div style={{fontSize:10,color:D.muted}}>{l}</div>
                <div style={{fontSize:14,fontWeight:600,color:D.text}}>{v}</div>
              </div>
            ))}
          </div>
          {aiBidResult.notes&&<AiBidNotes notes={aiBidResult.notes}/>}
        </div>

        <div style={{...st.card,marginBottom:0,border:`1px solid ${D.warning}44`,background:D.warning+"08"}}>
          <div style={{fontSize:14,fontWeight:600,color:D.warning,marginBottom:6}}>📋 Does this job require a permit?</div>
          {aiBidResult.permitNote&&<div style={{fontSize:12,color:D.muted,marginBottom:12,lineHeight:1.5}}>{aiBidResult.permitNote}</div>}
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button style={{...st.btn("primary"),marginLeft:0,flex:1,padding:"12px 0",fontSize:14,background:D.success,border:"none"}} onClick={()=>applyAiBid(true)}>
              ✓ Yes — Add Permit (+$500)
            </button>
            <button style={{...st.btn(),marginLeft:0,flex:1,padding:"12px 0",fontSize:14}} onClick={()=>applyAiBid(false)}>
              No Permit Needed
            </button>
          </div>
          <button style={{...st.btn(),marginLeft:0,width:"100%",marginTop:8,fontSize:12,color:D.muted}} onClick={()=>applyAiBid(false)}>
            Add Later
          </button>
        </div>
      </>}

    </div></Overlay>}

    {/* AI Bid Paywall modal */}
    {showAiPaywall&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:32,width:"min(460px,95vw)",textAlign:"center"}}>
      <div style={{fontSize:48,marginBottom:12}}>🔒</div>
      <div style={{fontFamily:"'Syne',sans-serif",fontSize:20,fontWeight:700,marginBottom:8,color:D.text}}>You've used all 3 free AI bids</div>
      <div style={{fontSize:14,color:D.muted,lineHeight:1.7,marginBottom:24}}>
        Subscribe to AmpBooks to unlock unlimited AI voice bidding, plus all features with no limits.
      </div>
      <div style={{background:D.surface,border:`1px solid ${D.border}`,borderRadius:10,padding:"16px 20px",marginBottom:20,textAlign:"left"}}>
        <div style={{fontSize:12,color:D.muted,marginBottom:10,fontWeight:500}}>Subscription plans from:</div>
        <div style={{display:"flex",gap:12}}>
          <div style={{flex:1,background:D.card,border:`1px solid ${D.border}`,borderRadius:8,padding:"12px 14px"}}>
            <div style={{fontSize:11,color:D.muted,marginBottom:4}}>BASIC</div>
            <div style={{fontSize:22,fontWeight:700,color:D.accent,fontFamily:"'Syne',sans-serif"}}>$49.99<span style={{fontSize:12,fontWeight:400,color:D.muted}}>/mo</span></div>
            <div style={{fontSize:11,color:D.muted,marginTop:4}}>Up to 5 employees</div>
          </div>
          <div style={{flex:1,background:D.card,border:`2px solid ${D.accent}`,borderRadius:8,padding:"12px 14px",position:"relative"}}>
            <div style={{position:"absolute",top:-1,left:"50%",transform:"translateX(-50%)",background:D.accent,color:"#fff",fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:"0 0 6px 6px",whiteSpace:"nowrap"}}>MOST POPULAR</div>
            <div style={{fontSize:11,color:D.accent,marginBottom:4,marginTop:6}}>PRO</div>
            <div style={{fontSize:22,fontWeight:700,color:D.accent,fontFamily:"'Syne',sans-serif"}}>$99.99<span style={{fontSize:12,fontWeight:400,color:D.muted}}>/mo</span></div>
            <div style={{fontSize:11,color:D.muted,marginTop:4}}>Up to 10 employees</div>
          </div>
        </div>
      </div>
      <button style={{...st.btn("primary"),marginLeft:0,width:"100%",padding:"13px 0",fontSize:15,marginBottom:10}} onClick={()=>{window.open("https://ampbooks.com/pricing","_blank");setShowAiPaywall(false);}}>
        View Plans & Subscribe →
      </button>
      <button style={{...st.btn(),marginLeft:0,width:"100%",padding:"10px 0",fontSize:13}} onClick={()=>setShowAiPaywall(false)}>
        Maybe later
      </button>
      <div style={{fontSize:11,color:D.muted,marginTop:12}}>You can still create bids manually using templates.</div>
    </div></Overlay>}

    {/* Bid edit modal */}
    {bidModal==="edit"&&<Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(700px,97vw)",maxHeight:"94vh",overflowY:"auto"}}>
      <MHdr title={bidData.id?"Edit Bid":"New Bid — "+bidData.template} onClose={()=>setBidModal(null)}/>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:14}}>
        <Field label="Bid #"><input style={st.input} value={bidData.number||""} onChange={e=>setBD("number",e.target.value)}/></Field>
        <Field label="Date"><input style={st.input} type="date" value={bidData.date||""} onChange={e=>setBD("date",e.target.value)}/></Field>
        <Field label="Status"><select style={st.input} value={bidData.status||"Pending"} onChange={e=>setBD("status",e.target.value)}>{BID_STATUS.map(s=><option key={s}>{s}</option>)}</select></Field>
        <div style={{gridColumn:"1/-1"}}><Field label="Client">
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <select style={{...st.input,flex:1}} value={bidData.clientId||""} onChange={e=>setBD("clientId",parseInt(e.target.value))}>
              {toArr(clients).length===0&&<option value="">— No clients yet —</option>}
              {toArr(clients).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button style={{...st.btn(),whiteSpace:"nowrap",flexShrink:0,background:"#1e3a5f",border:"1px solid #2d5f9e",color:"#7eb8f7"}} onClick={()=>openClientModal(null,(c)=>setBD("clientId",c.id))}>+ New Client</button>
          </div>
        </Field></div>
        
        <div style={{gridColumn:"1/-1"}}><Field label="Scope of Work / Description"><textarea style={{...st.input,height:60,resize:"vertical"}} value={bidData.description||""} onChange={e=>setBD("description",e.target.value)}/></Field></div>
      </div>

      {/* Job conditions — affect labor hours */}
      <div style={{background:D.surface,borderRadius:8,padding:"12px 14px",marginBottom:16,border:`1px solid ${D.border}`}}>
        <div style={{fontSize:12,fontWeight:500,color:D.muted,marginBottom:10}}>⏱ Labor Adjustment Factors</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
          <Field label="Job Type">
            <select style={st.input} value={bidData.jobType||"Remodel"} onChange={e=>{
              const jt=e.target.value;
              const oldFactor=LABOR_FACTORS.jobType[bidData.jobType||"Remodel"]||1;
              const newFactor=LABOR_FACTORS.jobType[jt]||1;
              const isNewConst=jt==="New Construction";
              const wasNewConst=(bidData.jobType||"Remodel")==="New Construction";
              // Swap box types when switching between new construction and remodel
              const BOX_SWAPS={
                "Single Gang Old Work Box":"Single Gang New Work Box",
                "Double Gang Old Work Box":"Double Gang New Work Box",
                "Triple Gang Old Work Box":"Triple Gang New Work Box",
                "Ceiling Box Old Work":"Ceiling Box New Work",
                "4\" Round Ceiling Box":"4\" Round Nail-On Ceiling Box",
                "1G Plastic Old Work Box w/Ears":"1G Plastic New Work Box",
                "2G Plastic Old Work Box w/Ears":"2G Plastic New Work Box",
              };
              const BOX_SWAPS_REV=Object.fromEntries(Object.entries(BOX_SWAPS).map(([k,v])=>[v,k]));
              setBidData(x=>({...x,jobType:jt,
                laborHours:{
                  master:Math.round((Number(x.laborHours?.master||0)/oldFactor*newFactor)*4)/4,
                  journeyman:Math.round((Number(x.laborHours?.journeyman||0)/oldFactor*newFactor)*4)/4,
                  apprentice:Math.round((Number(x.laborHours?.apprentice ?? x.laborHours?.helper ?? 0)/oldFactor*newFactor)*4)/4,
                },
                materials:toArr(x.materials).map(m=>{
                  if(isNewConst&&BOX_SWAPS[m.desc]){
                    const newDesc=BOX_SWAPS[m.desc];
                    const newCost=MAT_CATALOG_FLAT.find(c=>c.desc===newDesc)?.cost||m.cost;
                    return{...m,desc:newDesc,cost:newCost};
                  }
                  if(!isNewConst&&BOX_SWAPS_REV[m.desc]){
                    const newDesc=BOX_SWAPS_REV[m.desc];
                    const newCost=MAT_CATALOG_FLAT.find(c=>c.desc===newDesc)?.cost||m.cost;
                    return{...m,desc:newDesc,cost:newCost};
                  }
                  return m;
                })
              }));
            }}>
              {Object.keys(LABOR_FACTORS.jobType).map(t=><option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Wire Type">
            <select style={st.input} value={bidData.wireType||"Romex (NM)"} onChange={e=>{
              const wt=e.target.value;
              const oldFactor=LABOR_FACTORS.wireType[bidData.wireType||"Romex (NM)"]||1;
              const newFactor=LABOR_FACTORS.wireType[wt]||1;
              setBidData(x=>({...x,wireType:wt,laborHours:{
                master:Math.round((Number(x.laborHours?.master||0)/oldFactor*newFactor)*4)/4,
                journeyman:Math.round((Number(x.laborHours?.journeyman||0)/oldFactor*newFactor)*4)/4,
                apprentice:Math.round((Number(x.laborHours?.apprentice ?? x.laborHours?.helper ?? 0)/oldFactor*newFactor)*4)/4,
              }}));
            }}>
              {Object.keys(LABOR_FACTORS.wireType).map(t=><option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Access / Structure">
            <select style={st.input} value={bidData.accessType||"Open/Easy Access"} onChange={e=>{
              const at=e.target.value;
              const oldFactor=LABOR_FACTORS.accessType[bidData.accessType||"Open/Easy Access"]||1;
              const newFactor=LABOR_FACTORS.accessType[at]||1;
              setBidData(x=>({...x,accessType:at,laborHours:{
                master:Math.round((Number(x.laborHours?.master||0)/oldFactor*newFactor)*4)/4,
                journeyman:Math.round((Number(x.laborHours?.journeyman||0)/oldFactor*newFactor)*4)/4,
                apprentice:Math.round((Number(x.laborHours?.apprentice ?? x.laborHours?.helper ?? 0)/oldFactor*newFactor)*4)/4,
              }}));
            }}>
              {Object.keys(LABOR_FACTORS.accessType).map(t=><option key={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <div style={{fontSize:11,color:D.muted,marginTop:6}}>
          Combined factor: <span style={{color:D.warning,fontWeight:600}}>
            {((LABOR_FACTORS.jobType[bidData.jobType||"Remodel"]||1)*(LABOR_FACTORS.wireType[bidData.wireType||"Romex (NM)"]||1)*(LABOR_FACTORS.accessType[bidData.accessType||"Open/Easy Access"]||1)).toFixed(2)}x
          </span> base hours
        </div>
      </div>

      {/* Labor */}
      <div style={{marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:500,color:D.text,marginBottom:8}}>Labor Hours</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
          {[{key:"journeyman",label:"Journeyman"},{key:"apprentice",label:"Apprentice"}].map(r=>(
            <div key={r.key} style={{background:D.surface,borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:11,color:D.muted,marginBottom:6}}>{r.label}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
                <Field label="Hours"><input style={st.input} type="number" min="0" step="0.5" value={bidData.laborHours?.[r.key]??0} onChange={e=>setBD("laborHours",{...bidData.laborHours,[r.key]:e.target.value})}/></Field>
                <Field label="Bill/hr ($)"><input style={st.input} type="number" min="0" value={bidData.laborRates?.[r.key]?.bill??defaultRates[r.key].bill} onChange={e=>setBD("laborRates",{...bidData.laborRates,[r.key]:{...bidData.laborRates?.[r.key],bill:e.target.value}})}/></Field>
              </div>
              {(Number(bidData.laborHours?.[r.key])||0)>0&&<div style={{fontSize:11,color:D.success}}>Billed: {fmt((Number(bidData.laborHours?.[r.key])||0)*(Number(bidData.laborRates?.[r.key]?.bill)||defaultRates[r.key].bill))}</div>}
            </div>
          ))}
        </div>
        {(Number(bidData.laborHours?.master)||0)>0
          ? <div style={{background:D.surface,borderRadius:8,padding:"10px 12px",marginTop:10}}>
              <div style={{fontSize:11,color:D.muted,marginBottom:6}}>Master Electrician</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
                <Field label="Hours"><input style={st.input} type="number" min="0" step="0.5" value={bidData.laborHours?.master??0} onChange={e=>setBD("laborHours",{...bidData.laborHours,master:e.target.value})}/></Field>
                <Field label="Bill/hr ($)"><input style={st.input} type="number" min="0" value={bidData.laborRates?.master?.bill??125} onChange={e=>setBD("laborRates",{...bidData.laborRates,master:{...bidData.laborRates?.master,bill:e.target.value}})}/></Field>
              </div>
              <div style={{fontSize:11,color:D.success}}>Billed: {fmt((Number(bidData.laborHours?.master)||0)*(Number(bidData.laborRates?.master?.bill)||125))}</div>
            </div>
          : <button style={{...st.btn(),marginLeft:0,marginTop:8,fontSize:12}} onClick={()=>setBD("laborHours",{...bidData.laborHours,master:1})}>+ Add Master Electrician</button>
        }
      </div>

      {/* Materials */}
      <div style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{fontSize:13,fontWeight:500,color:D.text}}>Materials Takeoff</div>
          <div style={{display:"flex",gap:6}}>
            <button style={{...st.btn("primary"),marginLeft:0,padding:"4px 12px",fontSize:12}} onClick={()=>setMatPicker(true)}>+ From Catalog</button>
            <button style={{...st.btn(),marginLeft:0,padding:"4px 12px",fontSize:12}} onClick={addMatBlank}>+ Custom</button>
            <button style={{...st.btn(),marginLeft:0,padding:"4px 12px",fontSize:12}} onClick={()=>setScanPrices(true)}>📷 Scan Invoice</button>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 36px",gap:6,marginBottom:4}}>
          {["Description","Qty","Unit","Unit Cost ($)",""].map(h=><div key={h} style={{fontSize:11,color:D.muted}}>{h}</div>)}
        </div>
        {toArr(bidData.materials).map(m=>(
          <div key={m.id} style={{display:"grid",gridTemplateColumns:"3fr 1fr 1fr 1.5fr 36px",gap:6,marginBottom:6}}>
            <input style={st.input} value={m.desc||""} onChange={e=>updateMat(m.id,"desc",e.target.value)} placeholder="Description"/>
            <input style={st.input} type="number" min="0" value={m.qty} onChange={e=>updateMat(m.id,"qty",e.target.value)}/>
            <select style={st.input} value={m.unit||"ea"} onChange={e=>updateMat(m.id,"unit",e.target.value)}>{UNITS.map(u=><option key={u}>{u}</option>)}</select>
            <input style={st.input} type="number" min="0" step="0.01" value={m.cost} onChange={e=>updateMat(m.id,"cost",e.target.value)}/>
            <button style={{background:D.danger,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontSize:14,padding:"0 8px"}} onClick={()=>removeMat(m.id)}>✕</button>
          </div>
        ))}
        {toArr(bidData.materials).length===0&&<div style={{color:D.muted,fontSize:12,textAlign:"center",padding:"12px 0"}}>No materials yet. Click "+ From Catalog" to add.</div>}
      </div>

      {/* Material catalog picker modal */}
      {matPicker&&<Overlay zIndex={200}><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(600px,97vw)",maxHeight:"85vh",overflowY:"auto"}}>
        <MHdr title="Add from Catalog" onClose={()=>setMatPicker(false)}/>
        {/* Category tabs */}
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
          {Object.keys(MAT_CATALOG).map(cat=>(
            <button key={cat} style={{...st.btn(),marginLeft:0,padding:"4px 10px",fontSize:11,background:matCategory===cat?D.accent:D.border,color:matCategory===cat?"#fff":D.text}} onClick={()=>setMatCategory(cat)}>{cat}</button>
          ))}
        </div>
        {/* Items in selected category */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {(MAT_CATALOG[matCategory]||[]).map(item=>{
            const price=getPrice(item.desc);
            const isUserPrice=matPrices&&matPrices[item.desc]!==undefined;
            const lastScan=matPriceHistory&&matPriceHistory[item.desc]&&matPriceHistory[item.desc][0];
            return(
              <div key={item.desc} style={{background:D.surface,borderRadius:7,padding:"8px 12px",cursor:"pointer",border:`1px solid ${isUserPrice?D.success+"44":D.border}`}} onClick={()=>addMatFromCatalog(item)}>
                <div style={{fontSize:12,fontWeight:500,marginBottom:2}}>{item.desc}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:11,color:D.muted}}>{item.unit}</span>
                  <span style={{fontSize:11,color:isUserPrice?D.success:D.muted,fontWeight:500}}>${price.toFixed(2)}/{item.unit}</span>
                </div>
                {isUserPrice&&<div style={{fontSize:10,color:D.success,marginTop:2}}>
                  ✓ Your price{lastScan?` · ${lastScan.supplier} · ${lastScan.date}`:""}
                </div>}
                {!isUserPrice&&<div style={{fontSize:10,color:D.muted,marginTop:2}}>Base catalog price</div>}
              </div>
            );
          })}
        </div>
      </div></Overlay>}

      {/* Scan invoice for price updates */}
      {scanPrices&&<Overlay zIndex={200}><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:"min(500px,95vw)"}}>
        <MHdr title="📷 Scan Invoice — Update My Prices" onClose={()=>{setScanPrices(false);setScanImg(null);setScanImgB64(null);setScanSupplier("");}}/>
        <div style={{fontSize:12,color:D.muted,marginBottom:14,lineHeight:1.6}}>Scan a supply house invoice to update <strong style={{color:D.text}}>your prices only</strong>. Base catalog prices are never changed — only your personal price overrides.</div>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:12,color:D.muted,marginBottom:4}}>Supplier name (optional)</div>
          <input style={st.input} value={scanSupplier} onChange={e=>setScanSupplier(e.target.value)} placeholder="e.g. Ferguson, Graybar, Home Depot"/>
        </div>
        <input ref={scanFileRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={loadScanFile}/>
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          <button style={{...st.btn("primary"),marginLeft:0}} onClick={()=>{scanFileRef.current.removeAttribute("capture");scanFileRef.current.click();}}>📁 Choose Photo</button>
          <button style={{...st.btn(),marginLeft:0}} onClick={()=>{scanFileRef.current.setAttribute("capture","environment");scanFileRef.current.click();}}>📷 Take Photo</button>
        </div>
        {scanImg&&<img src={scanImg} alt="invoice" style={{maxWidth:"100%",maxHeight:200,borderRadius:8,objectFit:"contain",background:"#000",marginBottom:12}}/>}
        {scanning&&<div style={{textAlign:"center",padding:"16px 0",color:D.accent}}>🔍 Reading invoice prices…</div>}
        {!aiConfigured&&<div style={{color:D.danger,fontSize:12,marginBottom:12}}>⚠ API key required. Add it in ⚙ Settings.</div>}
        <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
          <button style={st.btn()} onClick={()=>{setScanPrices(false);setScanImg(null);setScanImgB64(null);}}>Cancel</button>
          {scanImgB64&&!scanning&&<button style={st.btn("primary")} onClick={scanInvoiceForPrices}>🔍 Update Prices</button>}
        </div>
      </div></Overlay>}

      {/* Fees & margins */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:14}}>
        <Field label="Permit Fee ($)"><input style={st.input} type="number" min="0" value={bidData.permitFee??0} onChange={e=>setBD("permitFee",e.target.value)}/></Field>
        <Field label="Overhead %"><input style={st.input} type="number" min="0" max="100" value={bidData.overheadPct??15} onChange={e=>setBD("overheadPct",e.target.value)}/></Field>
        <Field label="Profit %"><input style={st.input} type="number" min="0" max="100" value={bidData.profitPct??20} onChange={e=>setBD("profitPct",e.target.value)}/></Field>
      </div>

      {/* Live total preview */}
      {(()=>{const c=calcBid(bidData);return(
        <div style={{padding:"12px 16px",background:"#1a2a1a",borderRadius:8,border:`1px solid ${D.success}33`,marginBottom:16,display:"flex",gap:20,flexWrap:"wrap"}}>
          {[["Labor",c.laborBilled,D.text],["Materials",c.matBilled,D.text],["Overhead",c.overhead,D.muted],["Profit",c.profit,D.warning],["Tax",c.tax,D.muted],["Total",c.grandTotal,D.success]].map(([label,val,color])=>(
            <div key={label}><div style={{fontSize:10,color:D.muted}}>{label}</div><div style={{fontSize:label==="Total"?16:13,fontWeight:label==="Total"?700:400,color}}>{fmt(val)}</div></div>
          ))}
        </div>
      );})()}

      <Field label="Notes"><input style={st.input} value={bidData.notes||""} onChange={e=>setBD("notes",e.target.value)} placeholder="Internal notes"/></Field>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
        <button style={st.btn()} onClick={()=>setBidModal(null)}>Cancel</button>
        <button style={st.btn("primary")} onClick={saveBid}>Save Bid</button>
      </div>
    </div></Overlay>}
  </div>);
}

      

